import { Routes } from '@angular/router';

export const blogRoutes: Routes = [
  {
    path: 'bybit-exchange-fix-api-overview',
    loadComponent: () =>
      import('./pages/2026/October/bybit-exchange-fix-api-overview/bybit-exchange-fix-api-overview.component').then(
        (m) => m.BybitExchangeFixApiOverviewComponent,
      ),
    data: {
      sitemap: {
        loc: 'bybit-exchange-fix-api-overview',
        lastmod: '2026-10-12',
      },
      meta: {
        loc: 'bybit-exchange-fix-api-overview',
        fileName: '/bybit-exchange-fix-api-overview',
        title: 'Bybit FIX API — Spot Scope, Limits and Session Gotchas',
        description:
          'Bybit launched a FIX 4.4 API for spot order entry on 1 September 2026. What the session covers, where it leaves FIX 4.4, and what a reconnect quietly loses.',
        keywords:
          'bybit fix api, bybit fix 4.4, bybit spot fix api, fix api crypto exchange, bybit order entry, crypto fix connectivity, institutional crypto trading',
        imgUrl: '/assets/blog-card-images/bybit-exchange-fix-api-overview.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-10-12',
        dateModified: '2026-10-12',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Does Bybit have a FIX API?',
            a: 'Yes. Bybit launched a FIX 4.4 API for spot trading on 1 September 2026, reachable at fix-oe.bybit.com on port 9000 over TLS.',
          },
          {
            q: 'Which products does Bybit FIX cover, and what is in its published scope?',
            a: 'The order messages require Category tag 30010 and the documented value is spot, and the launch scope published by Bybit covers new order, amend and cancel.',
          },
          {
            q: 'How do I get access to Bybit FIX API?',
            a: 'Access requires whitelist onboarding at UID or institutional ID granularity, requested through a Bybit relationship manager, and an API key that is self-generated RSA with FIX API enabled.',
          },
          {
            q: 'Does Bybit FIX support resend requests?',
            a: 'No. ResendRequest tag 35=2 and SequenceReset tag 35=4 are not implemented, sequence numbers restart from 1 on every connection, and messages unconsumed at disconnect are not retransmitted.',
          },
          {
            q: 'How do I amend an order on Bybit FIX?',
            a: 'Through AtomicReplaceRequest, message type XAR. The standard OrderCancelReplaceRequest, message type G, is not supported.',
          },
          {
            q: 'Does the FIX API have its own rate limits?',
            a: 'No. The FIX API draws on the same rate limit pool as REST V5, and remaining weight is reported in tags 30007, 30008 and 30009.',
          },
        ],
      },
    },
  },
  {
    path: 'deribit-exchange-fix-api-overview',
    loadComponent: () =>
      import('./pages/2026/October/deribit-exchange-fix-api-overview/deribit-exchange-fix-api-overview.component').then(
        (m) => m.DeribitExchangeFixApiOverviewComponent,
      ),
    data: {
      sitemap: {
        loc: 'deribit-exchange-fix-api-overview',
        lastmod: '2026-10-09',
      },
      meta: {
        loc: 'deribit-exchange-fix-api-overview',
        fileName: '/deribit-exchange-fix-api-overview',
        title: 'Deribit FIX API Overview — Spot Routing and Order Types',
        description:
          'Deribit FIX API is a FIX 4.4 subset. Most spot is routed to Coinbase with no routing flag over FIX, and Starbase adds a second FIX 5.0 SP2 drop copy feed.',
        keywords:
          'Deribit FIX API, Deribit FIX 4.4, Deribit spot routing, Coinbase routed spot, Deribit Starbase, FIX drop copy, Deribit order types, crypto exchange FIX API',
        imgUrl: '/assets/blog-card-images/deribit-exchange-fix-api-overview.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-10-09',
        dateModified: '2026-10-09',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Does Deribit have a FIX API and what version is it',
            a: 'Yes. Deribit operates a first-party FIX API built as a subset of FIX 4.4 that also includes some FIX 5.0 tags and several custom Deribit tags.',
          },
          {
            q: 'How do I connect to the Deribit FIX API',
            a: 'Production is fix.deribit.com on port 9881 for raw tcp and 9883 for ssl, and the test environment is fix-test.deribit.com on the same two ports. Deribit has retired access to the FIX endpoint through www.deribit.com.',
          },
          {
            q: 'Are Deribit spot orders matched on Deribit',
            a: 'Some are and most are not. Most spot instruments are routed to Coinbase Exchange for matching, while a small number continue to be matched on the Deribit engine. Derivatives are never routed.',
          },
          {
            q: 'How do I tell whether a Deribit spot instrument is routed to Coinbase',
            a: 'Not over FIX. Security List returns both kinds as ordinary FXSPOT entries, so the routing flag has to be read from the JSON-RPC methods public slash get_instrument or public slash get_instruments, which return is_cbe_routed true for routed instruments.',
          },
          {
            q: 'Why does my Deribit market data request return no trades',
            a: 'On a spot instrument routed to Coinbase, MDEntryType 269 equals 2 for trades is rejected, so snapshots and incremental refreshes never carry trade entries for it. Bid and offer are still served, fed from the full Coinbase book.',
          },
          {
            q: 'Why was my post only order rejected on Deribit spot',
            a: 'On a routed instrument post only is accepted only with the reject flag, ExecInst 18 equals 6A. A bare 6 is rejected with post_only_not_allowed, because the price of a routed post only order is never adjusted into the spread.',
          },
          {
            q: 'Does Deribit FIX still support RFQ',
            a: 'No. The RFQ messages were removed in the release of 7 October 2025.',
          },
          {
            q: 'In what units is a Deribit FIX order quantity expressed',
            a: 'In contracts by default. QtyType 854 can be set to Units, in which case the quantity is in USD for perpetual and inverse futures, in the base currency coin for linear futures, and in the amount of cryptocurrency for options, and the server converts it to contracts when the order is placed.',
          },
          {
            q: 'Does Deribit use more than one FIX version',
            a: 'Yes. The main Deribit FIX API is a subset of FIX 4.4, while the Starbase FIX Drop Copy feed runs on FIX 5.0 SP2. They are separate paths with separate credentials. Order entry on Starbase runs over a binary SBE protocol rather than FIX, while FIX on Starbase is the drop copy feed.',
          },
          {
            q: 'Can I place an options order in implied volatility terms on Deribit FIX',
            a: 'Yes. DeribitAdvOrderType 100012 set to 0 marks an implied volatility order where the price defines a fixed implied volatility in percent, and set to 1 marks a USD order, which is not supported for linear options.',
          },
        ],
      },
    },
  },
  {
    path: 'kraken-exchange-fix-api-overview',
    loadComponent: () =>
      import('./pages/2026/October/kraken-exchange-fix-api-overview/kraken-exchange-fix-api-overview.component').then(
        (m) => m.KrakenExchangeFixApiOverviewComponent,
      ),
    data: {
      sitemap: {
        loc: 'kraken-exchange-fix-api-overview',
        lastmod: '2026-10-09',
      },
      meta: {
        loc: 'kraken-exchange-fix-api-overview',
        fileName: '/kraken-exchange-fix-api-overview',
        title: 'Kraken FIX API Overview — Capabilities and Caveats',
        description:
          'A guide to the Kraken FIX 4.4 API for Spot and Derivatives — how to get access, how sessions and ports work, what it supports and what it leaves to REST.',
        keywords:
          'Kraken FIX API, Kraken FIX 4.4, Kraken FIX, Kraken FIX ports, Kraken L3 market data, Kraken Spot FIX, Kraken Derivatives FIX, institutional crypto trading',
        imgUrl: '/assets/blog-card-images/kraken-exchange-fix-api-overview.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-10-07',
        dateModified: '2026-10-09',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Does Kraken support FIX?',
            a: 'Yes. Kraken operates a FIX 4.4 API covering both Spot and Derivatives markets, aimed at institutional and HFT clients, with access arranged through an Account Manager.',
          },
          {
            q: 'What FIX version does Kraken use?',
            a: 'FIX 4.4. Every Kraken documentation page covering the API states 4.4, for both Spot and Derivatives.',
          },
          {
            q: 'What can the Kraken FIX API do?',
            a: 'Reference data, market data at L2 and L3, order entry, order status and trading session status, on both Spot and Derivatives. It carries no account data, so balances, history and funding stay on REST.',
          },
          {
            q: 'Does Kraken FIX provide L3 order book data?',
            a: 'Yes, on port 4005 for Spot. The L3 feed carries order IDs, which lets fills in ExecutionReports be matched deterministically against book entries. Kraken states that is not possible with L2 data.',
          },
          {
            q: 'Does Kraken Unified FIX use one session for Spot and Derivatives?',
            a: 'Kraken describes it that way on some pages, but the rest of the documentation points to one session per market. Spot Trading and Derivatives Trading use different ports, Derivatives sessions carry a DRV suffix on the SenderCompID, and the two markets run on separate trading engines. Unified best describes the credential set rather than the transport.',
          },
          {
            q: 'Do I need separate credentials for Kraken Spot and Derivatives FIX?',
            a: 'The Unified FIX reference says one Spot FIX API Key authorises both. The Authentication guide recommends separate keys for Spot and Derivatives. Derivatives sessions use a SenderCompID with a DRV suffix in either case.',
          },
          {
            q: 'Can I cancel a Kraken order on a different FIX session than the one that placed it?',
            a: 'Not with a single order cancel. OrderCancelRequest only affects orders placed on the same FIX session. OrderMassCancelRequest, tag 35 equals q, cancels everything on the account regardless of origin, and REST or WebSocket can cancel from any source.',
          },
          {
            q: 'Does Kraken FIX support order amends?',
            a: 'On Spot yes, via OrderCancelReplaceRequest, tag 35 equals G. On Derivatives the Kraken documentation is inconsistent. The FIX introduction marks amend as coming soon, while the protocol comparison guide lists it as available over Unified FIX. Confirm with your Account Manager.',
          },
          {
            q: 'Does amending an order on Kraken lose queue priority?',
            a: 'Reducing quantity only preserves time priority. Increasing quantity or changing price moves the order to the back of the queue at that price.',
          },
          {
            q: 'Is Cancel on Disconnect available on Kraken FIX?',
            a: 'Yes, and it is enabled by default on every FIX trading session. Kraken documentation is inconsistent on configuration, describing it both as set per session on the Logon message via tag 8674 and as configured during onboarding.',
          },
          {
            q: 'Does Kraken FIX support TWAP or other algo orders?',
            a: 'Not yet. Synthetic order types are described as not currently available via FIX, with a future Algo Engine planned.',
          },
        ],
      },
    },
  },
  {
    path: 'coinbase-exchange-fix-api-overview',
    loadComponent: () =>
      import('./pages/2026/October/coinbase-exchange-fix-api-overview/coinbase-exchange-fix-api-overview.component').then(
        (m) => m.CoinbaseExchangeFixApiOverviewComponent,
      ),
    data: {
      sitemap: {
        loc: 'coinbase-exchange-fix-api-overview',
        lastmod: '2026-10-07',
      },
      meta: {
        loc: 'coinbase-exchange-fix-api-overview',
        fileName: '/coinbase-exchange-fix-api-overview',
        title: 'Coinbase Exchange FIX API Overview — FIX 5.0 SP2 Guide',
        description:
          'Coinbase Exchange FIX API overview — the FIX 5.0 SP2 session model, order entry semantics, market data, and the documented conflicts that break integrations.',
        keywords:
          'coinbase exchange fix api, coinbase fix 5.0 sp2, coinbase fix session, coinbase fix logon, coinbase fix cancel reason, coinbase fix drop copy, coinbase fix market data, crypto exchange fix api',
        imgUrl: '/assets/blog-card-images/coinbase-exchange-fix-api-overview.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-10-05',
        dateModified: '2026-10-07',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What FIX version does Coinbase Exchange use',
            a: 'Coinbase Exchange uses FIX 5.0 SP2 on a FIXT.1.1 session layer for order entry, market data and drop copy. The application version is declared on tag 1137 with the value 9.',
          },
          {
            q: 'Does Coinbase Exchange still support FIX 4.2',
            a: 'No. The FIX 4.2 order entry gateway was deprecated on 3 June 2025 and no longer appears among supported endpoints.',
          },
          {
            q: 'How many FIX APIs does Coinbase have',
            a: 'Five products expose different interfaces. Coinbase Exchange, Coinbase Prime, Coinbase International Exchange and Coinbase Derivatives each run their own FIX gateway on their own baseline, and Coinbase Advanced Trade offers REST and WebSocket only.',
          },
          {
            q: 'Does Coinbase Exchange FIX support message replay',
            a: 'The order entry specification documents ResendRequest with a maximum of 1000 messages per request, while the connectivity page states that resend requests are not supported. The two pages disagree and an integration should tolerate either behaviour.',
          },
          {
            q: 'How do I find out why Coinbase cancelled my order',
            a: 'The cancel reason arrives in tag 58 on the execution report, coded 101 for time in force, 102 for self-trade prevention, 103 for admin, 104 for price bound order protection, 105 for insufficient funds, 106 for insufficient liquidity and 107 for broker.',
          },
          {
            q: 'Why did my Coinbase order shrink without a cancel request',
            a: 'Self-trade prevention decremented it. The execution report carries ExecType D for restated with ExecRestatementReason 5, and the behaviour is chosen per order on tag 7928.',
          },
          {
            q: 'What is the difference between the snapshot enabled and snapshot disabled market data gateways',
            a: 'The snapshot enabled gateway sends Market Data Snapshot Full Refresh messages on subscription. The snapshot disabled gateway sends incrementals only, so the client builds the initial book from a REST order book snapshot and replays queued incrementals.',
          },
          {
            q: 'How many API keys do I need for Coinbase Exchange FIX',
            a: 'One per simultaneous connection. Reusing a single key across concurrent connections returns an error, and a profile supports up to 75 connections.',
          },
          {
            q: 'Why is my Coinbase FIX logon rejected with a signature error',
            a: 'Check three things — sequence numbers with leading zeros, SendingTime fractional seconds that are not exactly three digits, and a system clock outside the five minute SendingTime window.',
          },
          {
            q: 'Can I reconcile Coinbase FIX fills against the public market data feed',
            a: 'Tag 83 in the order entry header carries the market data feed sequence number for the same event, so fills can be aligned with the public feed without matching on timestamps.',
          },
        ],
      },
    },
  },
  {
    path: 'binance-spot-fix-api',
    loadComponent: () =>
      import('./pages/2026/September/binance-spot-fix-api/binance-spot-fix-api.component').then(
        (m) => m.BinanceSpotFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'binance-spot-fix-api',
        lastmod: '2026-09-30',
      },
      meta: {
        loc: 'binance-spot-fix-api',
        fileName: '/binance-spot-fix-api',
        title: 'Binance Spot FIX API — Sessions, Limits and Gotchas',
        description:
          'Binance Spot FIX 4.4 explained — three session types, Ed25519 only auth, no resend requests, no order status query, plus rate limits and integration traps.',
        keywords:
          'binance fix api, binance spot fix, fix 4.4 crypto, ed25519 fix, drop copy session, fix order entry, crypto exchange fix protocol',
        imgUrl: '/assets/blog-card-images/binance-spot-fix-api.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-09-30',
        dateModified: '2026-09-30',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Does Binance have a FIX API?',
            a: 'Yes. Binance provides a first-party FIX API for its Spot exchange, with separate Order Entry, Drop Copy, and Market Data sessions.',
          },
          {
            q: 'Does Binance support FIX for Futures?',
            a: 'The Binance Spot FIX API can only be used with the Spot exchange.',
          },
          { q: 'What FIX version does Binance use?', a: 'Binance Spot FIX runs on FIX 4.4.' },
          {
            q: 'What authentication does Binance FIX require?',
            a: 'FIX sessions accept Ed25519 API keys only, so an Ed25519 key with the FIX permission must be created.',
          },
          {
            q: 'Does Binance FIX support resend requests?',
            a: 'No. Binance documents resend requests as not currently supported, so recovery is done by reconnecting rather than by FIX message retransmission.',
          },
          {
            q: 'How do I query order status on Binance FIX?',
            a: 'You cannot. The FIX order entry session has no order status request message, so order state is read from the Drop Copy session or from the REST API.',
          },
          {
            q: 'What are the Binance FIX rate limits?',
            a: 'Order Entry allows 10,000 messages per 10 seconds, Drop Copy 60 messages per 60 seconds, and Market Data 2,000 messages per 60 seconds with at most 1,000 streams per connection.',
          },
          {
            q: 'Why does my Binance FIX logon fail?',
            a: 'The most common causes are a key that is not Ed25519, a missing FIX permission on the key, a SenderCompID longer than 8 characters, and a reconnect attempted within twice the heartbeat interval after a disconnect without Logout.',
          },
          {
            q: 'How many self-trade prevention modes does Binance Spot support?',
            a: 'Binance Spot documents six modes, NONE, EXPIRE_TAKER, EXPIRE_MAKER, EXPIRE_BOTH, DECREMENT, and TRANSFER.',
          },
        ],
      },
    },
  },
  {
    path: 'crypto-liquidity-for-mt4-and-mt5-brokers',
    loadComponent: () =>
      import('./pages/2026/September/crypto-liquidity-for-mt4-and-mt5-brokers/crypto-liquidity-for-mt4-and-mt5-brokers.component').then(
        (m) => m.CryptoLiquidityForMt4AndMt5BrokersComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-liquidity-for-mt4-and-mt5-brokers',
        lastmod: '2026-09-21',
      },
      meta: {
        loc: 'crypto-liquidity-for-mt4-and-mt5-brokers',
        fileName: '/crypto-liquidity-for-mt4-and-mt5-brokers',
        title: 'Crypto Liquidity for MT4 and MT5 Brokers',
        description:
          'Crypto liquidity for MT4 and MT5 brokers over FIX 4.4 — market data and execution across 30+ exchanges through one integration into the bridge you already run.',
        keywords:
          'crypto liquidity for MT4, crypto liquidity for MT5, MetaTrader crypto liquidity, FIX 4.4 crypto liquidity, crypto liquidity bridge MT5, MT4 MT5 crypto broker',
        imgUrl: '/assets/blog-card-images/crypto-liquidity-for-mt4-and-mt5-brokers.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'page',
        datePublished: '2026-09-21',
        dateModified: '2026-09-21',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Does this work with MT4, or only MT5',
            a: 'Both. MT5 has the Gateway API, MT4 needs a third-party bridge — either way the FIX session terminates at the bridge, not at the platform.',
          },
          {
            q: 'Do we need accounts on every exchange',
            a: 'Yes. You trade on your own venue accounts and supply the API credentials, so the trading relationship stays between you and the exchange.',
          },
          {
            q: 'How long does it take',
            a: 'A day to a week in most cases. If you already run an integrated bridge or hub, you are at the short end of that range.',
          },
          {
            q: 'Which bridge do we need',
            a: 'If you already run Brokeree Liquidity Bridge, Your Bourse or oneZero, none — the integration exists. Otherwise any bridge that speaks FIX 4.4 works.',
          },
        ],
      },
    },
  },
  {
    path: 'coupled-crypto-trading-stacks',
    loadComponent: () =>
      import('./pages/2026/July/coupled-crypto-trading-stacks/coupled-crypto-trading-stacks.component').then(
        (m) => m.CoupledCryptoTradingStacksComponent,
      ),
    data: {
      sitemap: {
        loc: 'coupled-crypto-trading-stacks',
        lastmod: '2026-07-28',
      },
      meta: {
        loc: 'coupled-crypto-trading-stacks',
        fileName: '/coupled-crypto-trading-stacks',
        title: 'Crypto Trading Stack Risk — When Layers Are Coupled',
        description:
          'Crypto trading stack risk explained — how execution, credit, custody, and connectivity get coupled to one counterparty, and why decoupling connectivity is hard',
        keywords:
          'crypto trading infrastructure, crypto trading stack, counterparty risk, execution credit custody, vendor-neutral connectivity, FIX 4.4 normalization, exchange symbology, order status normalization',
        imgUrl: '/assets/blog-card-images/coupled-crypto-trading-stacks.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-07-28',
        dateModified: '2026-07-28',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What does it mean to decouple layers in a crypto trading stack',
            a: 'It means keeping execution, connectivity, credit, and custody as separate functions with separate providers, so a failure in one does not automatically take down the others.',
          },
          {
            q: 'What kind of failure was the BlockFills Chapter 11 filing',
            a: 'It was a credit and counterparty failure in the liquidity and financing layer, not a technology outage. Clients were affected because they reached the market through the same counterparty.',
          },
          {
            q: 'Does using a connectivity provider protect my assets',
            a: 'No. Connectivity and normalization are a non-custodial layer. Custody and credit are separate layers with separate risk and are not covered by connectivity.',
          },
          {
            q: 'Why is normalizing order status across exchanges hard',
            a: 'Because venues use different order-status vocabularies, including venue-native states like Binance EXPIRED_IN_MATCH that map only to a generic Expired status in FIX 4.4, so a normalization layer must decide how to represent them and where to carry the reason.',
          },
          {
            q: 'What is EXPIRED_IN_MATCH on Binance',
            a: 'It is a Binance order status produced by self-trade prevention.',
          },
        ],
      },
    },
  },
  {
    path: 'hidden-cost-multi-exchange-connectivity',
    loadComponent: () =>
      import('./pages/2026/July/hidden-cost-multi-exchange-connectivity/hidden-cost-multi-exchange-connectivity.component').then(
        (m) => m.HiddenCostMultiExchangeConnectivityComponent,
      ),
    data: {
      sitemap: {
        loc: 'hidden-cost-multi-exchange-connectivity',
        lastmod: '2026-07-28',
      },
      meta: {
        loc: 'hidden-cost-multi-exchange-connectivity',
        fileName: '/hidden-cost-multi-exchange-connectivity',
        title: 'Crypto Exchange Connectivity — Build vs Buy True Cost',
        description:
          'The real cost of building multi-exchange connectivity is not the first integration but keeping each venue working as its API, rate limits and symbols change.',
        keywords:
          'multi-exchange connectivity, build vs buy crypto connectivity, cost of maintaining exchange API integrations, is CCXT reliable, crypto exchange API changes, FIX connectivity provider',
        imgUrl: '/assets/blog-card-images/hidden-cost-multi-exchange-connectivity.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-07-20',
        dateModified: '2026-07-28',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Is CCXT reliable for production trading?',
            a: 'CCXT is a mature and widely used library, but its own documentation states it is provided with no warranties and on an as-is basis, and the project carries hundreds of open issues and changes daily. It can be reliable in production when you pin versions, test against your specific venues, and treat it as a maintained dependency rather than a finished product.',
          },
          {
            q: 'How often do crypto exchange APIs change?',
            a: 'Major venues publish changelogs that update on their own schedules, and active ones such as Bybit, OKX, Gemini, and Binance log changes on a near-weekly basis, ranging from renamed fields to new required parameters to altered WebSocket messages. There is no single published industry-wide rate of change.',
          },
          {
            q: 'What is the real cost of maintaining exchange API integrations?',
            a: 'The recurring cost is not the first build but keeping every integration working as each venue independently changes authentication, rate limits, symbols, and streaming behavior, which scales with the number of venues and with how often each one changes.',
          },
          {
            q: 'Should I build or buy multi-exchange connectivity?',
            a: 'Build if you have the engineering headcount and need total control; buy if you would rather your engineers spend limited time on strategy and execution than on tracking venue changelogs.',
          },
          {
            q: 'Does using a library remove the maintenance burden?',
            a: 'No, it moves the burden. You still track, test, pin, and occasionally patch the dependency, and cross-venue normalization differences remain your responsibility.',
          },
        ],
      },
    },
  },
  {
    path: 'binance-order-types-spot-vs-futures',
    loadComponent: () =>
      import('./pages/2026/July/binance-order-types-spot-vs-futures/binance-order-types-spot-vs-futures.component').then(
        (m) => m.BinanceOrderTypesSpotVsFuturesComponent,
      ),
    data: {
      sitemap: {
        loc: 'binance-order-types-spot-vs-futures',
        lastmod: '2026-07-16',
      },
      meta: {
        loc: 'binance-order-types-spot-vs-futures',
        fileName: '/binance-order-types-spot-vs-futures',
        title: 'Binance Order Types and Execution — Spot vs Futures',
        description:
          'Binance order types and execution differ across Spot and Futures — post-only, iceberg, time-in-force, STP and rejection codes compared for platform teams.',
        keywords:
          'binance order types, binance spot vs futures, LIMIT_MAKER, GTX post only, EXPIRED_IN_MATCH, binance time in force, binance futures reduceOnly, binance rate limits',
        imgUrl: '/assets/blog-card-images/binance-order-types-spot-vs-futures.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-07-16',
        dateModified: '2026-07-16',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'How do you place a post-only order on Binance Futures?',
            a: 'On USD-M Futures post-only is set through time-in-force GTX on a limit order not through a separate order type.',
          },
          {
            q: 'Why is there no LIMIT_MAKER on Binance Futures?',
            a: 'Futures expresses maker-only through the GTX time-in-force instead of a LIMIT_MAKER order type which is a Spot only order type.',
          },
          {
            q: 'What does EXPIRED_IN_MATCH mean on Binance?',
            a: 'It means an order was expired by self-trade prevention because it would have matched an order from the same account or trade group rather than filled or canceled normally.',
          },
          {
            q: 'Does Binance Futures support iceberg orders?',
            a: 'No native iceberg parameter exists on USD-M Futures while Spot supports iceberg through the icebergQty parameter.',
          },
          {
            q: 'Which time-in-force values does Binance Spot support?',
            a: 'Binance Spot supports exactly GTC IOC and FOK while USD-M Futures adds GTX GTD and RPI.',
          },
          {
            q: 'Why did my Binance Futures stop order start returning -4120?',
            a: 'Since 2025-12-09 conditional order types moved to the Algo Order endpoints and the plain New Order endpoint rejects them with code -4120.',
          },
        ],
      },
    },
  },
  {
    path: 'delivering-reference-data-over-fix-security-list',
    loadComponent: () =>
      import('./pages/2026/July/delivering-reference-data-over-fix-security-list/delivering-reference-data-over-fix-security-list.component').then(
        (m) => m.DeliveringReferenceDataOverFixSecurityListComponent,
      ),
    data: {
      sitemap: {
        loc: 'delivering-reference-data-over-fix-security-list',
        lastmod: '2026-07-10',
      },
      meta: {
        loc: 'delivering-reference-data-over-fix-security-list',
        fileName: '/delivering-reference-data-over-fix-security-list',
        title: 'FIX SecurityList 35=y — Crypto Reference Data Guide',
        description:
          'FIX SecurityList 35=y delivers instrument reference data answering SecurityListRequest 35=x. Crypto venues fill Symbol 55 and SecurityExchange 207 differently.',
        keywords:
          'FIX SecurityList, 35=y, SecurityListRequest, 35=x, reference data, Symbol 55, SecurityExchange 207, crypto FIX API, FIX 4.4 reference data, instrument normalization',
        imgUrl: '/assets/blog-card-images/delivering-reference-data-over-fix-security-list.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-07-10',
        dateModified: '2026-07-10',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is FIX message 35=y',
            a: 'It is the SecurityList message used to return a list of instruments and their trading parameters in response to a SecurityListRequest 35=x.',
          },
          {
            q: 'What is the difference between SecurityList and SecurityListRequest',
            a: 'SecurityListRequest 35=x is the query a client sends. SecurityList 35=y is the response the venue returns.',
          },
          {
            q: 'Which FIX fields identify an instrument in a SecurityList',
            a: 'Symbol 55 gives the ticker string and SecurityExchange 207 gives the venue, both inside the Instrument component block.',
          },
          {
            q: 'How does a client know the SecurityList is complete',
            a: 'By reading TotNoRelatedSym 393 and the LastFragment 893 flag, since large lists are split across multiple messages.',
          },
          {
            q: 'Do all crypto venues use the same symbol format in tag 55',
            a: 'No. Bullish uses a concatenated form such as BTCUSD, and other venues use their own delimiters and suffixes, so tag 55 alone is not portable across venues.',
          },
          {
            q: 'How do I get instruments that were listed after my initial SecurityList',
            a: 'It depends on the venue. Some support a proactive update message while others, such as Polymarket US, require re-sending SecurityListRequest 35=x.',
          },
        ],
      },
    },
  },
  {
    path: 'which-crypto-exchanges-support-fix',
    loadComponent: () =>
      import('./pages/2026/July/which-crypto-exchanges-support-fix/which-crypto-exchanges-support-fix.component').then(
        (m) => m.WhichCryptoExchangesSupportFixComponent,
      ),
    data: {
      sitemap: {
        loc: 'which-crypto-exchanges-support-fix',
        lastmod: '2026-10-12',
      },
      meta: {
        loc: 'which-crypto-exchanges-support-fix',
        fileName: '/which-crypto-exchanges-support-fix',
        title: 'Which Crypto Exchanges Support FIX — 2026 Guide',
        description:
          'Which crypto exchanges support FIX API in 2026, with FIX versions and session types compared across Binance, Coinbase, Kraken, Gemini and Deribit.',
        keywords:
          'crypto exchanges FIX API, FIX protocol crypto, FIX 4.4, FIX 5.0, Binance FIX, Coinbase FIX, Kraken FIX, Gemini FIX, Deribit FIX, institutional crypto trading',
        imgUrl: '/assets/blog-card-images/which-crypto-exchanges-support-fix.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-07-02',
        dateModified: '2026-10-12',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'Which crypto exchanges support FIX API',
            a: 'Binance (Spot), Bybit (Spot), Coinbase International, Coinbase Exchange, Coinbase Prime, Kraken, Gemini, Deribit, Crypto.com, Bitstamp, and CEX.IO document first-party FIX APIs. Bitfinex offers FIX through an open-source gateway. OKX offers FIX through institutional onboarding rather than public documentation.',
          },
          {
            q: 'What FIX version do crypto exchanges use',
            a: 'Most use FIX 4.4, including Binance, Kraken, Gemini, Deribit, Crypto.com and CEX.IO. Coinbase International uses FIX 5.0, while Coinbase Exchange and Coinbase Prime run FIX 4.2 baselines with FIX 5.0 gateways rolled out or in beta.',
          },
          {
            q: 'Does Binance have a FIX API',
            a: 'Yes — for the Spot exchange only, with separate order entry, drop copy, and market data sessions on FIX 4.4, and it requires Ed25519 API keys.See Binance Spot FIX API for the full session and authentication detail.',
          },
          {
            q: 'Does Coinbase use FIX 4.4',
            a: 'No. Coinbase International runs FIX 5.0, Coinbase Exchange runs FIX 4.2 for order entry and FIX 5.0 for market data, and Coinbase Prime runs FIX 4.2 with a FIX 5.0 API in beta.',
          },
          {
            q: 'Do OKX and Bybit support FIX',
            a: 'Bybit documents a FIX 4.4 order-entry API for Spot, with access granted only to whitelisted accounts. OKX does not publish a FIX specification, but offers FIX to approved institutional clients through its Rubix platform. In both cases FIX access runs through onboarding rather than through a self-service API key.',
          },
          {
            q: 'Is there a single FIX standard for crypto',
            a: 'No universally adopted crypto FIX profile exists. The FIX Trading Community runs a Digital Asset Working Group on the topic, but today versions, authentication and session rules differ from venue to venue.',
          },
        ],
      },
    },
  },
  {
    path: 'crypto-trading-infrastructure-build-vs-buy',
    loadComponent: () =>
      import('./pages/2026/June/crypto-trading-infrastructure-build-vs-buy/crypto-trading-infrastructure-build-vs-buy.component').then(
        (m) => m.CryptoTradingInfrastructureBuildVsBuyComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-trading-infrastructure-build-vs-buy',
        lastmod: '2026-06-29',
      },
      meta: {
        loc: 'crypto-trading-infrastructure-build-vs-buy',
        fileName: '/crypto-trading-infrastructure-build-vs-buy',
        title: 'Crypto Trading Infrastructure — Build vs Buy',
        description:
          'Build vs buy crypto trading infrastructure — the real cost is maintaining 30+ exchange integrations forever, not the first build. When each path wins.',
        keywords:
          'build vs buy crypto trading infrastructure, crypto OEMS, multi-exchange connectivity, symbol normalization, FIX 4.4, exchange API maintenance',
        imgUrl: '/assets/blog-card-images/crypto-trading-infrastructure-build-vs-buy.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-06-29',
        dateModified: '2026-06-29',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is the difference between building and buying crypto trading infrastructure?',
            a: 'Building means writing and operating your own connectivity to each exchange (routing, market data, symbol normalization, session management and state reconciliation) and maintaining it as the venues change. Buying means licensing a managed OEMS that exposes all venues through one interface and absorbs that maintenance.',
          },
          {
            q: 'Why is building multi-exchange connectivity so expensive to maintain?',
            a: 'Because the cost is recurring, not one-time. Each exchange has its own API, symbol format, authentication, rate limits and schedule of breaking changes, and there is no universally adopted market-data standard across crypto venues. One infrastructure vendor estimates that teams spend 20 to 30 percent of their development time just keeping existing integrations working, and exchanges such as Binance publish dated mandatory changes to their FIX APIs that an in-house integration must track.',
          },
          {
            q: 'Why can the same crypto pair have different symbols on different exchanges?',
            a: 'There is no industry standard for instrument naming. The Bitcoin to US dollar pair is BTC-USD on Coinbase and appears as XBT/USD, XBTUSD or XXBTZUSD on Kraken depending on the interface. Robust systems maintain a normalized symbol directory that maps every venue native format to one canonical form.',
          },
          {
            q: 'When should a trading firm build its own infrastructure instead of buying?',
            a: 'Build when the connectivity layer itself is your product or your competitive edge, such as an exchange aggregator or a latency-arbitrage shop. Buy when your edge is the strategy or product built on top of execution. Most firms take the hybrid path, buying the connectivity and building the strategy.',
          },
          {
            q: 'What does a managed crypto OEMS replace?',
            a: 'It replaces the per-venue adapters, the symbol normalization directory, the connectivity monitoring, the state reconciliation and the key management and session handling you would otherwise build and staff yourself, exposing all of it through a single standardized interface.',
          },
        ],
      },
    },
  },
  {
    path: 'fix-vs-rest-vs-websocket-for-crypto-trading',
    loadComponent: () =>
      import('./pages/2026/June/fix-vs-rest-vs-websocket-for-crypto-trading/fix-vs-rest-vs-websocket-for-crypto-trading.component').then(
        (m) => m.FixVsRestVsWebsocketForCryptoTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'fix-vs-rest-vs-websocket-for-crypto-trading',
        lastmod: '2026-06-24',
      },
      meta: {
        loc: 'fix-vs-rest-vs-websocket-for-crypto-trading',
        fileName: '/fix-vs-rest-vs-websocket-for-crypto-trading',
        title: 'FIX vs REST vs WebSocket — Which to Use for Crypto Trading',
        description:
          'How FIX, REST, and WebSocket differ for crypto trading, what each protocol is built for, and why desks pair WebSocket data with REST setup and a FIX order path.',
        keywords:
          'fix vs rest, fix vs websocket, rest vs websocket crypto, crypto trading api, fix protocol, order entry, market data, rate limits',
        imgUrl: '/assets/blog-card-images/fix-vs-rest-vs-websocket-for-crypto-trading.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-06-24',
        dateModified: '2026-06-24',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is the difference between FIX, REST, and WebSocket for crypto trading?',
            a: 'REST is a request and response protocol best for one-off actions and queries. WebSocket is a server-push stream best for market data and status updates. FIX is a stateful sequenced session built for order entry and execution reporting. They are complementary, not interchangeable.',
          },
          {
            q: 'Is FIX better than REST for crypto trading?',
            a: 'For the order path FIX is generally the stronger fit because it is asynchronous, delivers ordered and recoverable messages, pushes execution reports without polling, and carries a client-assigned order ID. REST remains better for account setup, one-off actions, and recovery.',
          },
          {
            q: 'Why is polling a REST API a problem for trading?',
            a: 'Polling means repeatedly asking the venue for order status in a loop. It consumes a shared rate-limit budget, competes with order placement for that budget, and still leaves a delay between when a fill happens and when the client next asks. On Binance spot all REST endpoints share 6,000 request-weight per minute per IP, and overuse can lead to an IP ban. Pushed updates over WebSocket or FIX avoid this.',
          },
          {
            q: 'Can I trade entirely over WebSocket?',
            a: 'Some venues allow order actions over WebSocket, but a raw WebSocket does not guarantee ordered delivery or message replay on its own, and update ordering across separate channels is not guaranteed. That makes it well suited to data and risky as the sole order-lifecycle channel without extra handling.',
          },
          {
            q: 'Do major crypto exchanges support FIX?',
            a: 'Yes. Coinbase, Kraken, and Binance all offer FIX order entry alongside REST and WebSocket APIs, with Kraken and Binance on FIX 4.4 and Coinbase order entry on FIX 5.0.',
          },
          {
            q: 'What is ClOrdID in FIX?',
            a: 'ClOrdID is tag 11, a client-assigned identifier placed on each order, cancel, and replace. The venue echoes it on every related execution report, so a client can match each fill or status change to the exact order that produced it.',
          },
          {
            q: 'When was the FIX protocol created?',
            a: 'FIX was created in 1992 by Fidelity Investments and Salomon Brothers for equity trading. It is now governed by the FIX Trading Community and used across asset classes worldwide, with the 2001 FIX 4.2 release still in common use.',
          },
        ],
      },
    },
  },
  {
    path: 'crypto-fix-api-how-it-works-and-which-exchanges-support-it',
    loadComponent: () =>
      import('./pages/2026/June/crypto-fix-api-how-it-works-and-which-exchanges-support-it/crypto-fix-api-how-it-works-and-which-exchanges-support-it.component').then(
        (m) => m.CryptoFixApiHowItWorksAndWhichExchangesSupportItComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-fix-api-how-it-works-and-which-exchanges-support-it',
        lastmod: '2026-10-12',
      },
      meta: {
        loc: 'crypto-fix-api-how-it-works-and-which-exchanges-support-it',
        fileName: '/crypto-fix-api-how-it-works-and-which-exchanges-support-it',
        title: 'Crypto FIX API — How It Works, Sessions and Message Types',
        description:
          'A crypto FIX API trades over the FIX protocol instead of REST or WebSocket. This guide covers sessions, message types and the order flow on crypto venues.',
        keywords:
          'crypto fix api, fix api crypto exchange, crypto exchange fix api, fix 4.4 crypto, fix protocol crypto, fix api vs rest api, fix vs websocket, coinbase fix api, kraken fix api, binance fix api, gemini fix api, fix order entry, drop copy, financial information exchange crypto',
        imgUrl: '/assets/blog-card-images/crypto-fix-api-how-it-works-and-which-exchanges-support-it.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-06-22',
        dateModified: '2026-10-12',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is a crypto FIX API?',
            a: 'It is an interface that lets a firm trade on a crypto exchange using the Financial Information eXchange protocol, a session-based tag-value messaging standard from traditional finance, instead of a REST or WebSocket API.',
          },
          {
            q: 'FIX API vs REST API for crypto — which is better?',
            a: 'Neither is universally better. FIX suits firms with existing FIX infrastructure that need low latency, session recovery, and standardized order semantics; REST and WebSocket are simpler, open to any account, and available on every venue. Many large exchanges offer only REST and WebSocket.',
          },
          {
            q: 'FIX vs WebSocket — what is the difference?',
            a: 'WebSocket is a general-purpose, full-duplex web protocol good for streaming market data to many clients. FIX is a financial-industry standard with a recoverable session layer, sequence numbers, and standardized order messages, built for order execution and reconciliation.',
          },
          {
            q: 'What is a drop copy session?',
            a: 'A read-only FIX session that receives copies of execution reports for an account, used by risk, compliance, and middle-office systems to monitor order activity without being able to place or cancel orders.',
          },
        ],
      },
    },
  },
  {
    path: 'crypto-instrument-reference-data',
    loadComponent: () =>
      import('./pages/2026/June/crypto-instrument-reference-data/crypto-instrument-reference-data.component').then(
        (m) => m.CryptoInstrumentReferenceDataComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-instrument-reference-data',
        lastmod: '2026-06-16',
      },
      meta: {
        loc: 'crypto-instrument-reference-data',
        fileName: '/crypto-instrument-reference-data',
        title: 'Crypto Instrument Reference Data — Tick, Lot, Notional',
        description:
          'Tick size, lot size, and min notional across Binance, Coinbase, and Kraken — the field names, endpoints, and why multi-venue systems must normalize them.',
        keywords:
          'crypto instrument reference data, tick size, lot size, minimum notional, exchange trading rules, symbol normalization, FIX SecurityList',
        imgUrl: '/assets/blog-card-images/crypto-instrument-reference-data.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-06-16',
        dateModified: '2026-06-16',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is the difference between tick size and lot size?',
            a: 'Tick size is the minimum increment for an order price; lot size is the minimum increment and minimum value for an order quantity. Tick size controls price granularity, lot size controls quantity granularity. An order must satisfy both independently.',
          },
          {
            q: 'What is minimum notional in crypto trading?',
            a: 'Minimum notional is the smallest allowed value of an order, calculated as price multiplied by quantity. An order can have a valid price and a valid quantity and still be rejected if its total value falls below the venue minimum notional.',
          },
          {
            q: 'Why does each exchange use different names for the same rules?',
            a: 'Each exchange designed its API independently, so the same concept appears under different field names and structures. Binance uses tickSize, stepSize, and minNotional inside filter objects; Coinbase uses quote_increment, base_increment, and quote_min_size as flat fields; Kraken uses tick_size, lot_decimals, and costmin per pair. Multi-venue systems normalize these to one internal schema.',
          },
          {
            q: 'Where do I get tick size and lot size for an exchange?',
            a: 'Each venue publishes them through a reference-data endpoint: Binance through exchangeInfo, Coinbase through the products endpoint, and Kraken through the AssetPairs endpoint. These values can change without notice, so they should be re-fetched regularly rather than cached permanently.',
          },
          {
            q: 'How do trading platforms handle reference data across many exchanges?',
            a: 'They build a normalization layer that maps every venue native format to one internal representation, refresh it continuously to handle drift, and validate orders against tick size, lot size, and minimum notional together. Axon Trade provides this as a normalized symbol and asset database delivered over FIX SecurityList.',
          },
        ],
      },
    },
  },
  {
    path: 'crypto-symbology',
    loadComponent: () =>
      import('./pages/2026/June/crypto-symbology/crypto-symbology.component').then((m) => m.CryptoSymbologyComponent),
    data: {
      sitemap: {
        loc: 'crypto-symbology',
        lastmod: '2026-06-12',
      },
      meta: {
        loc: 'crypto-symbology',
        fileName: '/crypto-symbology',
        title: 'Crypto Symbology — Why Exchanges Name Markets Differently',
        description:
          'Crypto symbology has no central standard — the same market is BTCUSDT, BTC-USD or XBTUSD across exchanges. Why it happens, and how normalization fixes it.',
        keywords:
          'crypto symbology, symbol normalization, crypto fix api, multi-exchange trading, crypto oems, normalized market data',
        imgUrl: '/assets/blog-card-images/crypto-symbology.png',
        og_type: 'article',
      },
      structuredData: {
        type: 'article',
        datePublished: '2026-06-12',
        dateModified: '2026-06-12',
        articleSection: 'Trading Infrastructure',
        faq: [
          {
            q: 'What is symbology in crypto trading?',
            a: 'It is the convention an exchange uses to name a tradable instrument, encoding the base asset, quote asset, and instrument type into a short string like BTC-USD or BTCUSDT. Because crypto has no central standard, symbology differs from venue to venue.',
          },
          {
            q: 'Why does Bitcoin have different symbols (BTC vs XBT) on different exchanges?',
            a: 'XBT follows ISO 4217, which prefixes non-country currencies with X (gold is XAU); BTC is the colloquial market standard. With no governing body, both spread. Kraken, for example, uses BTC on its interface and XBT in its API and logs.',
          },
          {
            q: 'What does BTCUSDT mean, and how do you split the base from the quote?',
            a: 'It means Bitcoin (BTC) priced in Tether (USDT), concatenated with no separator. A machine can only split it correctly by checking against a maintained list of valid assets, because the string alone is ambiguous — which is exactly why normalization is needed.',
          },
          {
            q: 'Is USDT the same as USD for symbology purposes?',
            a: 'No. They are different assets with different risk profiles, and a correct normalization scheme keeps them distinct even though both get loosely called dollars.',
          },
          {
            q: 'Can the same symbol mean two different instruments?',
            a: 'Yes. On Bybit, BTCUSDT is both the spot pair and the linear perpetual; only a separate category field tells them apart. Treating the symbol as a unique key without that context routes orders to the wrong instrument.',
          },
          {
            q: 'How do you normalize symbols across multiple crypto exchanges?',
            a: 'Define one canonical naming scheme and maintain a bidirectional mapping to each venue native format — resolving equivalent codes, separating genuinely different ones, encoding instrument type explicitly, and tracking reference-data changes so the mapping stays correct over time.',
          },
        ],
      },
    },
  },
  {
    path: 'what-is-a-crypto-oms-and-ems',
    loadComponent: () =>
      import('./pages/2025/November/what-is-a-crypto-oms-and-ems/what-is-a-crypto-oms-and-ems.component').then(
        (m) => m.WhatIsACryptoOmsAndEmsComponent,
      ),
    data: {
      sitemap: {
        loc: 'what-is-a-crypto-oms-and-ems',
        lastmod: '2025-11-26',
      },
      meta: {
        loc: 'what-is-a-crypto-oms-and-ems',
        fileName: '/what-is-a-crypto-oms-and-ems',
        title: 'What Is a Crypto OMS and EMS and Why Institutional Desks Rely on Them',
        description:
          'This article explores how OMS and EMS systems support institutional crypto trading by managing liquidity fragmentation, enforcing risk controls, and improving execution across multiple venues.',
        keywords:
          'crypto OEMS, OMS vs EMS, institutional crypto trading, crypto FIX API, smart order routing, execution management system, order management crypto, Axon Trade OMS, crypto trading infrastructure, fragmented crypto liquidity, multi-exchange trading, crypto order routing, institutional crypto tools, FIX protocol crypto, crypto EMS system, crypto OMS system, crypto trade automation, exchange connectivity crypto, crypto trading platform for funds',
        imgUrl: '/assets/blog-card-images/what-is-a-crypto-oms-and-ems.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'rebalancing-at-scale',
    loadComponent: () =>
      import('./pages/2025/October/rebalancing-at-scale/rebalancing-at-scale.component').then(
        (m) => m.RebalancingAtScaleComponent,
      ),
    data: {
      sitemap: {
        loc: 'rebalancing-at-scale',
        lastmod: '2025-10-28',
      },
      meta: {
        loc: 'rebalancing-at-scale',
        fileName: '/rebalancing-at-scale',
        title: 'Rebalancing at Scale',
        description:
          'Rebalancing at scale is the coordination of portfolio adjustments across venues, collateral types, and assets. It demands deterministic systems, data freshness, and precise execution planning.',
        keywords:
          'rebalancing engine, crypto portfolio management, multi-venue execution, collateral efficiency, asset allocation, liquidity routing, trading infrastructure, institutional crypto',
        imgUrl: '/assets/blog-card-images/rebalancing-at-scale.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'fill-reason-codes-that-matter',
    loadComponent: () =>
      import('./pages/2025/October/fill-reason-codes-that-matter/fill-reason-codes-that-matter.component').then(
        (m) => m.FillReasonCodesThatMatterComponent,
      ),
    data: {
      sitemap: {
        loc: 'fill-reason-codes-that-matter',
        lastmod: '2025-10-23',
      },
      meta: {
        loc: 'fill-reason-codes-that-matter',
        fileName: '/fill-reason-codes-that-matter',
        title: 'Fill Reason Codes That Matter',
        description:
          'Fill reason codes transform raw execution data into structured insight. They explain why fills occur, enable accurate TCA, and form the backbone of adaptive trading strategies.',
        keywords:
          'fill reason codes, crypto execution analysis, trading system telemetry, TCA, passive vs aggressive fills, execution metadata, order routing, trade analytics',
        imgUrl: '/assets/blog-card-images/fill-reason-codes-that-matter.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'latency-budgeting-by-component',
    loadComponent: () =>
      import('./pages/2025/October/latency-budgeting-by-component/latency-budgeting-by-component.component').then(
        (m) => m.LatencyBudgetingByComponentComponent,
      ),
    data: {
      sitemap: {
        loc: 'latency-budgeting-by-component',
        lastmod: '2025-10-16',
      },
      meta: {
        loc: 'latency-budgeting-by-component',
        fileName: '/latency-budgeting-by-component',
        title: 'Latency Budgeting by Component',
        description:
          'Latency budgeting decomposes end-to-end execution time into measurable components, assigning ownership and maintaining visibility across trading systems.',
        keywords:
          'latency budgeting, execution latency, trading infrastructure, performance monitoring, co-location, network delay, FIX gateway, trading system design',
        imgUrl: '/assets/blog-card-images/latency-budgeting-by-component.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'clock-discipline-for-trading-systems',
    loadComponent: () =>
      import('./pages/2025/October/clock-discipline-for-trading-systems/clock-discipline-for-trading-systems.component').then(
        (m) => m.ClockDisciplineForTradingSystemsComponent,
      ),
    data: {
      sitemap: {
        loc: 'clock-discipline-for-trading-systems',
        lastmod: '2025-10-08',
      },
      meta: {
        loc: 'clock-discipline-for-trading-systems',
        fileName: '/clock-discipline-for-trading-systems',
        title: 'Clock Discipline for Trading Systems',
        description:
          'Clock discipline is a foundational element of crypto trading infrastructure. Precise synchronization ensures reliable latency, accurate TCA, and consistent risk management across distributed systems.',
        keywords:
          'clock synchronization, PTP, NTP drift, crypto trading infrastructure, latency measurement, deterministic time, timestamp accuracy, execution systems',
        imgUrl: '/assets/blog-card-images/clock-discipline-for-trading-systems.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'fees-rebates-and-maker-taker-math',
    loadComponent: () =>
      import('./pages/2025/September/fees-rebates-and-maker-taker-math/fees-rebates-and-maker-taker-math.component').then(
        (m) => m.FeesRebatesAndMakerTakerMathComponent,
      ),
    data: {
      sitemap: {
        loc: 'fees-rebates-and-maker-taker-math',
        lastmod: '2025-09-25',
      },
      meta: {
        loc: 'fees-rebates-and-maker-taker-math',
        fileName: '/fees-rebates-and-maker-taker-math',
        title: 'Fees, Rebates, and Maker/Taker Math',
        description:
          'In crypto trading, fees and rebates are not surface numbers. Maker/taker math defines liquidity, routing, and effective cost, shaping execution outcomes and long-term portfolio performance.',
        keywords:
          'crypto trading fees, maker taker rebates, execution cost, fee tiers, smart order routing, transaction cost analysis, effective trading cost, liquidity incentives',
        imgUrl: '/assets/blog-card-images/fees-rebates-and-maker-taker-math.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'basket-and-multi-leg-coordination',
    loadComponent: () =>
      import('./pages/2025/September/basket-and-multi-leg-coordination/basket-and-multi-leg-coordination.component').then(
        (m) => m.BasketAndMultiLegCoordinationComponent,
      ),
    data: {
      sitemap: {
        loc: 'basket-and-multi-leg-coordination',
        lastmod: '2025-09-23',
      },
      meta: {
        loc: 'basket-and-multi-leg-coordination',
        fileName: '/basket-and-multi-leg-coordination',
        title: 'Basket and Multi-Leg Coordination',
        description:
          'Basket and multi-leg coordination in crypto trading ensures synchronized execution of correlated orders, reducing risk drift and slippage across fragmented exchanges.',
        keywords:
          'basket execution, multi-leg coordination, crypto trading, portfolio hedging, synthetic exposures, spread strategies, execution risk, TCA',
        imgUrl: '/assets/blog-card-images/basket-and-multi-leg-coordination.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'cross-exchange-latent-dependencies-in-portfolios',
    loadComponent: () =>
      import('./pages/2025/September/cross-exchange-latent-dependencies-in-portfolios/cross-exchange-latent-dependencies-in-portfolios.component').then(
        (m) => m.CrossExchangeLatentDependenciesInPortfoliosComponent,
      ),
    data: {
      sitemap: {
        loc: 'cross-exchange-latent-dependencies-in-portfolios',
        lastmod: '2025-09-18',
      },
      meta: {
        loc: 'cross-exchange-latent-dependencies-in-portfolios',
        fileName: '/cross-exchange-latent-dependencies-in-portfolios',
        title: 'Cross-Exchange Latent Dependencies in Portfolios',
        description:
          'Latent cross-exchange dependencies—funding divergence, transfer delays, and liquidity shocks—shape crypto portfolios in hidden ways. Learn how to monitor and adapt.',
        keywords:
          'cross-exchange dependencies, crypto contagion, funding arbitrage, collateral latency, liquidity spillover, systemic risk, portfolio resilience, stablecoin stress',
        imgUrl: '/assets/blog-card-images/cross-exchange-latent-dependencies-in-portfolios.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'portfolio-margin-and-collateral-routing',
    loadComponent: () =>
      import('./pages/2025/September/portfolio-margin-and-collateral-routing/portfolio-margin-and-collateral-routing.component').then(
        (m) => m.PortfolioMarginAndCollateralRoutingComponent,
      ),
    data: {
      sitemap: {
        loc: 'portfolio-margin-and-collateral-routing',
        lastmod: '2025-09-16',
      },
      meta: {
        loc: 'portfolio-margin-and-collateral-routing',
        fileName: '/portfolio-margin-and-collateral-routing',
        title: 'Portfolio Margin & Collateral Routing',
        description:
          'How unified portfolio margin and collateral routing optimize capital, reduce friction, and enable dynamic execution across crypto futures, cross-margin, and spot with minimal transfers',
        keywords:
          'portfolio margin, collateral routing, crypto derivatives execution, capital efficiency, unrealized PnL offset, margin engine, auto collateral routing, cross-product margin',
        imgUrl: '/assets/blog-card-images/portfolio-margin-and-collateral-routing.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'twap-vwap-that-survives-crypto-microstructure',
    loadComponent: () =>
      import('./pages/2025/September/twap-vwap-that-survives-crypto-microstructure/twap-vwap-that-survives-crypto-microstructure.component').then(
        (m) => m.TwapVwapThatSurvivesCryptoMicrostructureComponent,
      ),
    data: {
      sitemap: {
        loc: 'twap-vwap-that-survives-crypto-microstructure',
        lastmod: '2025-09-12',
      },
      meta: {
        loc: 'twap-vwap-that-survives-crypto-microstructure',
        fileName: '/twap-vwap-that-survives-crypto-microstructure',
        title: 'TWAP/VWAP That Survives Crypto Microstructure',
        description:
          'Practical TWAP/VWAP design for crypto\u2236 liquidity-aware cadence, queue-sensitive tactics, and venue-limit guards that keep execution stable under noisy microstructure.',
        keywords:
          'TWAP, VWAP, crypto microstructure, order book liquidity, rate limits, queue position, execution strategy, TCA',
        imgUrl: '/assets/blog-card-images/twap-vwap-that-survives-crypto-microstructure.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'funding-perps-and-execution-windows',
    loadComponent: () =>
      import('./pages/2025/September/funding-perps-and-execution-windows/funding-perps-and-execution-windows.component').then(
        (m) => m.FundingPerpsAndExecutionWindowsComponent,
      ),
    data: {
      sitemap: {
        loc: 'funding-perps-and-execution-windows',
        lastmod: '2025-09-09',
      },
      meta: {
        loc: 'funding-perps-and-execution-windows',
        fileName: '/funding-perps-and-execution-windows',
        title: 'Funding, Perps, and Execution Windows',
        description:
          'Funding cycles in perpetual futures create execution windows that shape liquidity, cost, and timing strategies for professional traders.',
        keywords:
          'perpetual futures, funding rate, execution windows, crypto trading, order book liquidity, funding cycles, execution strategy, TCA',
        imgUrl: '/assets/blog-card-images/funding-perps-and-execution-windows.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'why-execution-risk-deserves-lts-own-pnl-line',
    loadComponent: () =>
      import('./pages/2025/August/why-execution-risk-deserves-lts-own-pnl-line/why-execution-risk-deserves-lts-own-pnl-line.component').then(
        (m) => m.WhyExecutionRiskDeservesLtsOwnPnlLineComponent,
      ),
    data: {
      sitemap: {
        loc: 'why-execution-risk-deserves-lts-own-pnl-line',
        lastmod: '2025-08-26',
      },
      meta: {
        loc: 'why-execution-risk-deserves-lts-own-pnl-line',
        fileName: '/why-execution-risk-deserves-lts-own-pnl-line',
        title: 'Why Execution Risk Deserves Its Own PnL Line',
        description:
          'Execution risk distorts performance metrics by introducing invisible friction between trading intent and actual outcome. This article outlines why execution impact deserves a dedicated PnL line and how to break it down across slippage, missed fills, routing delays, and quote decay. ',
        keywords:
          'execution risk, execution PnL attribution, slippage analysis, missed fills, order routing delay, queue position loss, microstructure slippage, latency impact on trading, signal-to-fill gap, performance attribution in trading, crypto trading latency, institutional-grade execution reporting',
        imgUrl: '/assets/blog-card-images/why-execution-risk-deserves-lts-own-pnl-line.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'why-position-management-is-often-more-latency-sensitive-than-execution',
    loadComponent: () =>
      import('./pages/2025/August/why-position-management-is-often-more-latency-sensitive-than-execution/why-position-management-is-often-more-latency-sensitive-than-execution.component').then(
        (m) => m.WhyPositionManagementIsOftenMoreLatencySensitiveThanExecutionComponent,
      ),
    data: {
      sitemap: {
        loc: 'why-position-management-is-often-more-latency-sensitive-than-execution',
        lastmod: '2025-08-21',
      },
      meta: {
        loc: 'why-position-management-is-often-more-latency-sensitive-than-execution',
        fileName: '/why-position-management-is-often-more-latency-sensitive-than-execution',
        title: 'Why Position Management Is Often More Latency Sensitive Than Execution',
        description:
          'Execution latency gets all the attention, but position updates can introduce just as much risk. This article explains why position management often creates more sensitivity in fast markets, especially in multi-venue and high-frequency strategies.',
        keywords:
          'position latency, trading infrastructure, real-time position tracking, risk management, execution latency, fill acknowledgment, cross-exchange trading, position drift, inventory skew, latency arbitrage',
        imgUrl: '/assets/blog-card-images/why-position-management-is-often-more-latency-sensitive-than-execution.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'what-most-smart-order-routers-get-wrong-about-microstructure',
    loadComponent: () =>
      import('./pages/2025/August/what-most-smart-order-routers-get-wrong-about-microstructure/what-most-smart-order-routers-get-wrong-about-microstructure.component').then(
        (m) => m.WhatMostSmartOrderRoutersGetWrongAboutMicrostructureComponent,
      ),
    data: {
      sitemap: {
        loc: 'what-most-smart-order-routers-get-wrong-about-microstructure',
        lastmod: '2025-08-19',
      },
      meta: {
        loc: 'what-most-smart-order-routers-get-wrong-about-microstructure',
        fileName: '/what-most-smart-order-routers-get-wrong-about-microstructure',
        title: 'What Most Smart Order Routers Get Wrong About Microstructure',
        description:
          'Many Smart Order Routers rely on surface-level price signals, but real execution edge depends on microstructure. This article breaks down how flawed assumptions in queue modeling, liquidity churn, and trade classification lead to missed fills and hidden slippage.',
        keywords:
          'smart order router, SOR, trading microstructure, liquidity churn, queue modeling, cancel-replace loop, fill probability, hidden slippage, execution algorithms, fragmented markets',
        imgUrl: '/assets/blog-card-images/what-most-smart-order-routers-get-wrong-about-microstructure.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'illusion-of-best-execution-in-fragmented-books',
    loadComponent: () =>
      import('./pages/2025/August/illusion-of-best-execution-in-fragmented-books/illusion-of-best-execution-in-fragmented-books.component').then(
        (m) => m.IllusionOfBestExecutionInFragmentedBooksComponent,
      ),
    data: {
      sitemap: {
        loc: 'illusion-of-best-execution-in-fragmented-books',
        lastmod: '2025-08-14',
      },
      meta: {
        loc: 'illusion-of-best-execution-in-fragmented-books',
        fileName: '/illusion-of-best-execution-in-fragmented-books',
        title: 'The Illusion of Best Execution in Fragmented Books',
        description:
          'Best execution in fragmented markets is rarely as objective as it seems. This article explains why quote visibility, venue behavior, and latency create a fundamental disconnect between the theoretical best price and the actual quality of execution.',
        keywords:
          'best execution, fragmented books, smart order routing, execution quality, crypto trading infrastructure, quote decay, latency arbitrage, order routing, digital asset execution, market fragmentation',
        imgUrl: '/assets/blog-card-images/illusion-of-best-execution-in-fragmented-books.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'latency-spikes-in-colocated-environments',
    loadComponent: () =>
      import('./pages/2025/August/latency-spikes-in-colocated-environments/latency-spikes-in-colocated-environments.component').then(
        (m) => m.LatencySpikesInColocatedEnvironmentsComponent,
      ),
    data: {
      sitemap: {
        loc: 'latency-spikes-in-colocated-environments',
        lastmod: '2025-08-12',
      },
      meta: {
        loc: 'latency-spikes-in-colocated-environments',
        fileName: '/latency-spikes-in-colocated-environments',
        title: 'Latency Spikes in Colocated Environments',
        description:
          'Latency in colocated trading environments is rarely as stable as expected. Microsecond-level spikes distort execution timing, alter queue positioning, and break latency-dependent trading models. This article examines the technical causes of these anomalies and their real consequences for trading performance.',
        keywords:
          'colocation latency, latency spikes, HFT infrastructure, microsecond latency, trading performance, low latency trading, order execution timing, queue position, FIX protocol latency, high-frequency trading risks',
        imgUrl: '/assets/blog-card-images/latency-spikes-in-colocated-environments.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'what-breaks-atomicity-in-multi-leg-execution-chains',
    loadComponent: () =>
      import('./pages/2025/August/what-breaks-atomicity-in-multi-leg-execution-chains/what-breaks-atomicity-in-multi-leg-execution-chains.component').then(
        (m) => m.WhatBreaksAtomicityInMultiLegExecutionChainsComponent,
      ),
    data: {
      sitemap: {
        loc: 'what-breaks-atomicity-in-multi-leg-execution-chains',
        lastmod: '2025-08-07',
      },
      meta: {
        loc: 'what-breaks-atomicity-in-multi-leg-execution-chains',
        fileName: '/what-breaks-atomicity-in-multi-leg-execution-chains',
        title: 'What Breaks Atomicity in Multi-Leg Execution Chains',
        description:
          'Atomicity in multi-leg execution is more fragile than most traders assume. This article explores how fill delays, venue mismatches, and FIX message ambiguity disrupt conditional strategies — and how to design systems that handle inconsistency without blowing up exposure.',
        keywords:
          'multi-leg execution, atomicity, order routing, conditional logic, fill acknowledgment, FIX protocol, execution chains, latency risk, probabilistic trading, trading infrastructure',
        imgUrl: '/assets/blog-card-images/what-breaks-atomicity-in-multi-leg-execution-chains.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'real-time-vs-eventual-consistency',
    loadComponent: () =>
      import('./pages/2025/August/real-time-vs-eventual-consistency/real-time-vs-eventual-consistency.component').then(
        (m) => m.RealTimeVsEventualConsistencyComponent,
      ),
    data: {
      sitemap: {
        loc: 'real-time-vs-eventual-consistency',
        lastmod: '2025-08-05',
      },
      meta: {
        loc: 'real-time-vs-eventual-consistency',
        fileName: '/real-time-vs-eventual-consistency',
        title: 'Real-Time vs Eventual Consistency in FIX Routing',
        description:
          'Why consistency models matter in FIX routing, how they impact execution, and what architectures are behind reliable state tracking',
        keywords:
          'FIX routing, real-time consistency, eventual consistency, order state, trading infrastructure, execution reliability, high-frequency trading systems, latency in trading, FIX protocol architecture',
        imgUrl: '/assets/blog-card-images/real-time-vs-eventual-consistency.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'price-time-priority-vs-speed-priority',
    loadComponent: () =>
      import('./pages/2025/July/price-time-priority-vs-speed-priority/price-time-priority-vs-speed-priority.component').then(
        (m) => m.PriceTimePriorityVsSpeedPriorityComponent,
      ),
    data: {
      sitemap: {
        loc: 'price-time-priority-vs-speed-priority',
        lastmod: '2025-07-29',
      },
      meta: {
        loc: 'price-time-priority-vs-speed-priority',
        fileName: '/price-time-priority-vs-speed-priority',
        title: 'Price-Time Priority vs Speed Priority',
        description:
          'Learn how different order matching models impact execution quality in crypto trading. Understand the structural differences between price-time and speed-priority systems, and how they shape latency sensitivity, queue behavior, and trading outcomes.',
        keywords:
          'price-time priority, speed priority, crypto matching engine, order execution, latency, trading infrastructure, queue modeling, order book behavior, HFT',
        imgUrl: '/assets/blog-card-images/price-time-priority-vs-speed-priority.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'cross-exchange-order-dependencies',
    loadComponent: () =>
      import('./pages/2025/July/cross-exchange-order-dependencies/cross-exchange-order-dependencies.component').then(
        (m) => m.CrossExchangeOrderDependenciesComponent,
      ),
    data: {
      sitemap: {
        loc: 'cross-exchange-order-dependencies',
        lastmod: '2025-07-24',
      },
      meta: {
        loc: 'cross-exchange-order-dependencies',
        fileName: '/cross-exchange-order-dependencies',
        title: 'Cross-Exchange Order Dependencies',
        description:
          'Cross-exchange strategies in crypto trading often break due to subtle mismatches between platforms. This article examines how dependency structures create fragile logic and lead to silent execution failures.',
        keywords:
          'cross-exchange order execution, crypto order dependency, smart order router crypto, multi-venue strategy risks, trading drift crypto, partial fill across exchanges, conditional order execution crypto, crypto execution latency, order chaining crypto, asynchronous crypto trading',
        imgUrl: '/assets/blog-card-images/cross-exchange-order-dependencies.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'the-execution-tax-of-small-orders',
    loadComponent: () =>
      import('./pages/2025/July/the-execution-tax-of-small-orders/the-execution-tax-of-small-orders.component').then(
        (m) => m.TheExecutionTaxOfSmallOrdersComponent,
      ),
    data: {
      sitemap: {
        loc: 'the-execution-tax-of-small-orders',
        lastmod: '2025-07-22',
      },
      meta: {
        loc: 'the-execution-tax-of-small-orders',
        fileName: '/the-execution-tax-of-small-orders',
        title: 'The Execution Tax of Small Orders',
        description:
          'Small orders in crypto may feel safer—but trading systems often deprioritize or penalize them. This article explores the invisible costs behind tiny trade size.',
        keywords:
          'small order execution crypto, crypto micro trade cost, fee floor crypto platforms, partial fills small size, matching engine priority rules, crypto spread slippage low volume, internalization small orders, execution latency crypto, smart routing thresholds trading',
        imgUrl: '/assets/blog-card-images/the-execution-tax-of-small-orders.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'why-pnl-is-not-a-single-number',
    loadComponent: () =>
      import('./pages/2025/July/why-pnl-is-not-a-single-number/why-pnl-is-not-a-single-number.component').then(
        (m) => m.WhyPnlIsNotASingleNumberComponent,
      ),
    data: {
      sitemap: {
        loc: 'why-pnl-is-not-a-single-number',
        lastmod: '2025-07-10',
      },
      meta: {
        loc: 'why-pnl-is-not-a-single-number',
        fileName: '/why-pnl-is-not-a-single-number',
        title: 'Why PnL Is Not a Single Number',
        description:
          'Why profit and loss in trading can’t be reduced to a single figure. Explore the many moving parts behind realized, unrealized, and synthetic PnL metrics.',
        keywords:
          'PnL metrics crypto, realized vs unrealized PnL, synthetic PnL crypto trading, crypto trading PnL calculation, hidden losses in crypto PnL, trading system accounting, mark-to-market crypto, funding-based PnL, slippage-adjusted PnL',
        imgUrl: '/assets/blog-card-images/why-pnl-is-not-a-single-number.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'systemic-neutrality-in-execution',
    loadComponent: () =>
      import('./pages/2025/July/systemic-neutrality-in-execution/systemic-neutrality-in-execution.component').then(
        (m) => m.SystemicNeutralityInExecutionComponent,
      ),
    data: {
      sitemap: {
        loc: 'systemic-neutrality-in-execution',
        lastmod: '2025-07-08',
      },
      meta: {
        loc: 'systemic-neutrality-in-execution',
        fileName: '/systemic-neutrality-in-execution',
        title: 'Systemic Neutrality in Execution',
        description:
          'What neutrality means inside trading systems. How platforms interpret zero exposure, and why position closure doesn’t always equal resolution.',
        keywords:
          'neutral position crypto, trading system neutrality, flat position behavior, execution state crypto, rebalancing exposure, post-trade system actions, margin neutrality crypto, platform execution logic, funding after position close',
        imgUrl: '/assets/blog-card-images/systemic-neutrality-in-execution.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'time-in-force-and-order-lifespan',
    loadComponent: () =>
      import('./pages/2025/July/time-in-force-and-order-lifespan/time-in-force-and-order-lifespan.component').then(
        (m) => m.TimeInForceAndOrderLifespanComponent,
      ),
    data: {
      sitemap: {
        loc: 'time-in-force-and-order-lifespan',
        lastmod: '2025-07-01',
      },
      meta: {
        loc: 'time-in-force-and-order-lifespan',
        fileName: '/time-in-force-and-order-lifespan',
        title: 'Time-in-Force and Order Lifespan in Execution Systems',
        description:
          'A precise look at how TIF flags like GTC, IOC, and FOK behave inside crypto matching engines under real-world conditions and execution load.',
        keywords:
          'Time in Force crypto, GTC IOC FOK, crypto matching engine behavior, execution flags, crypto order lifespan, partial fills and TIF, crypto automated trading risk, system-level execution logic',
        imgUrl: '/assets/blog-card-images/time-in-force-and-order-lifespan.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'your-bourse-and-axon-trade-partner',
    loadComponent: () =>
      import('./pages/2025/May/your-bourse-and-axon-trade-partner/your-bourse-and-axon-trade-partner.component').then(
        (m) => m.YourBourseAndAxonTradePartnerComponent,
      ),
    data: {
      sitemap: {
        loc: 'your-bourse-and-axon-trade-partner',
        lastmod: '2025-05-29',
      },
      meta: {
        loc: 'your-bourse-and-axon-trade-partner',
        fileName: '/your-bourse-and-axon-trade-partner',
        title: 'Your Bourse and Axon Trade Partner to Expand Access to Digital Asset Markets',
        description:
          'A partnership overview between Axon Trade and Your Bourse focused on digital asset market access. The piece outlines how Your Bourse clients can now access Axon Trade’s execution and market data tools, benefiting from FIX integration, liquidity aggregation, and cross-venue trading. It also highlights what each company brings to the table in the institutional trading space.',
        keywords:
          'Axon Trade, Your Bourse, digital asset EMS, liquidity aggregation crypto, institutional crypto trading tools, FIX API trading crypto, crypto trading partnership, multi-venue execution digital assets, Axon Trade market data, Your Bourse pricing engine, crypto execution management system, institutional crypto infrastructure, trading platform connectivity, crypto market access, EMS for digital assets, multi-asset trading technology, cross-exchange crypto execution, crypto trading infrastructure, trading venue connectivity, crypto prime services, FIX protocol crypto, electronic trading crypto',
        imgUrl: '/assets/blog-card-images/your-bourse-and-axon-trade-partner.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'execution-during-cascades',
    loadComponent: () =>
      import('./pages/2025/May/execution-during-cascades/execution-during-cascades.component').then(
        (m) => m.ExecutionDuringCascadesComponent,
      ),
    data: {
      sitemap: {
        loc: 'execution-during-cascades',
        lastmod: '2025-05-20',
      },
      meta: {
        loc: 'execution-during-cascades',
        fileName: '/execution-during-cascades',
        title: 'Execution During Cascades',
        description:
          'What trading systems actually do during liquidation cascades. How platforms reorder execution, delay feedback, and degrade quietly under pressure.',
        keywords:
          'crypto order execution, liquidation cascade trading, stop order behavior crypto, order book collapse, latency in trading systems, matching engine overload, trading UI vs backend, crypto platform risk, structural fragility trading, cascading markets execution',
        imgUrl: '/assets/blog-card-images/execution-during-cascades.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'reduce-only-orders-in-crypto',
    loadComponent: () =>
      import('./pages/2025/May/reduce-only-orders-in-crypto/reduce-only-orders-in-crypto.component').then(
        (m) => m.ReduceOnlyOrdersInCryptoComponent,
      ),
    data: {
      sitemap: {
        loc: 'reduce-only-orders-in-crypto',
        lastmod: '2025-05-15',
      },
      meta: {
        loc: 'reduce-only-orders-in-crypto',
        fileName: '/reduce-only-orders-in-crypto',
        title: 'Reduce-Only Orders in Crypto',
        description:
          'A close analysis of reduce-only orders in crypto trading. How they behave across platforms, what they prevent, and where assumptions still break.',
        keywords:
          'reduce-only order crypto, reduce-only Binance, reduce-only Bybit, execution control trading, trading system discipline, position reversal prevention, crypto exit safety, automated order protection, stop-loss behavior, position integrity crypto',
        imgUrl: '/assets/blog-card-images/reduce-only-orders-in-crypto.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'partial-fills-and-the-fragility-of-assumptions',
    loadComponent: () =>
      import('./pages/2025/May/partial-fills-and-the-fragility-of-assumptions/partial-fills-and-the-fragility-of-assumptions.component').then(
        (m) => m.PartialFillsAndTheFragilityOfAssumptionsComponent,
      ),
    data: {
      sitemap: {
        loc: 'partial-fills-and-the-fragility-of-assumptions',
        lastmod: '2025-05-12',
      },
      meta: {
        loc: 'partial-fills-and-the-fragility-of-assumptions',
        fileName: '/partial-fills-and-the-fragility-of-assumptions',
        title: 'Partial Fills and the Fragility of Assumptions',
        description:
          'Analyzing how partial fills occur in crypto trading, their impact on automated strategies, and the structural risks they introduce across different platforms.',
        keywords:
          'partial fills crypto, crypto order execution, trading automation risks, order slippage, execution latency, crypto trading strategies, order book depth, trading platform behavior',
        imgUrl: '/assets/blog-card-images/partial-fills-and-the-fragility-of-assumptions.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'oco-orders',
    loadComponent: () => import('./pages/2025/May/oco-orders/oco-orders.component').then((m) => m.OcoOrdersComponent),
    data: {
      sitemap: {
        loc: 'oco-orders',
        lastmod: '2025-05-08',
      },
      meta: {
        loc: 'oco-orders',
        fileName: '/oco-orders',
        title: 'OCO Orders\u2236 Execution, Failure, and Use at Scale',
        description:
          'How OCO logic works across platforms, where it fails under load, and what professional traders do to control risk when one outcome cancels another',
        keywords:
          'oco crypto, one cancels the other order, oco trading crypto, stop loss and take profit crypto, oco binance, oco bybit, crypto order management, crypto exit strategy, linked orders, structured risk crypto',
        imgUrl: '/assets/blog-card-images/oco-orders.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'crypto-arbitrage-strategies',
    loadComponent: () =>
      import('./pages/2025/May/crypto-arbitrage-strategies/crypto-arbitrage-strategies.component').then(
        (m) => m.CryptoArbitrageStrategiesComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-arbitrage-strategies',
        lastmod: '2025-05-05',
      },
      meta: {
        loc: 'crypto-arbitrage-strategies',
        fileName: '/crypto-arbitrage-strategies',
        title: 'Crypto Arbitrage Strategies',
        description:
          'A realistic look at crypto arbitrage in 2025. From cross-exchange spreads to hidden edges, here’s what traders still use—and what they’ve stopped pretending works.',
        keywords:
          'crypto arbitrage, arbitrage trading crypto, cross exchange arbitrage, triangular arbitrage, arbitrage strategies 2025, crypto market inefficiencies, latency arbitrage, funding rate arbitrage, how to trade arbitrage crypto, arbitrage bots crypto',
        imgUrl: '/assets/blog-card-images/crypto-arbitrage-strategies.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'swing-trading-in-crypto',
    loadComponent: () =>
      import('./pages/2025/May/swing-trading-in-crypto/swing-trading-in-crypto.component').then(
        (m) => m.SwingTradingInCryptoComponent,
      ),
    data: {
      sitemap: {
        loc: 'swing-trading-in-crypto',
        lastmod: '2025-05-01',
      },
      meta: {
        loc: 'swing-trading-in-crypto',
        fileName: '/swing-trading-in-crypto',
        title: 'Swing Trading in Crypto\u2236 Strategy, Timing, and Risk',
        description:
          'A closer look at swing trading in crypto markets. Learn how traders use short-to-medium-term moves to find entries, manage risk, and avoid overtrading.',
        keywords:
          'swing trading crypto, crypto swing strategy, how to swing trade bitcoin, crypto trading techniques, short term crypto trades, crypto entry signals, crypto risk control, technical analysis swing trading',
        imgUrl: '/assets/blog-card-images/swing-trading-in-crypto.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'what-makes-bitcoin-different-from-other-cryptocurrencies',
    loadComponent: () =>
      import('./pages/2025/April/what-makes-bitcoin-different-from-other-cryptocurrencies/what-makes-bitcoin-different-from-other-cryptocurrencies.component').then(
        (m) => m.WhatMakesBitcoinDifferentFromOtherCryptocurrenciesComponent,
      ),
    data: {
      sitemap: {
        loc: 'what-makes-bitcoin-different-from-other-cryptocurrencies',
        lastmod: '2025-04-29',
      },
      meta: {
        loc: 'what-makes-bitcoin-different-from-other-cryptocurrencies',
        fileName: '/what-makes-bitcoin-different-from-other-cryptocurrencies',
        title: 'What Makes Bitcoin Different from Other Cryptocurrencies',
        description:
          'Bitcoin works under a different set of assumptions than most cryptocurrencies. This article looks at how its structure, rules, and culture make it stand apart.',
        keywords:
          'bitcoin vs other cryptocurrencies, bitcoin vs altcoins, bitcoin unique features, why bitcoin is different, bitcoin supply limit, bitcoin proof of work, bitcoin decentralization, bitcoin monetary policy, bitcoin vs ethereum, bitcoin protocol stability',
        imgUrl: '/assets/blog-card-images/what-makes-bitcoin-different-from-other-cryptocurrencies.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'vwap-in-crypto-trading',
    loadComponent: () =>
      import('./pages/2025/April/vwap-in-crypto-trading/vwap-in-crypto-trading.component').then(
        (m) => m.VwapInCryptoTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'vwap-in-crypto-trading',
        lastmod: '2025-04-24',
      },
      meta: {
        loc: 'vwap-in-crypto-trading',
        fileName: '/vwap-in-crypto-trading',
        title: 'VWAP in Crypto Trading',
        description:
          'Learn how VWAP works in crypto. This guide covers volume-weighted execution, how to use VWAP to manage large orders, and when it makes sense to apply it.',
        keywords:
          'vwap crypto, volume weighted average price crypto, vwap strategy trading, vwap indicator crypto, crypto execution strategy, vwap vs twap, large order execution, crypto trading benchmarks, trade execution analysis, institutional crypto trading',
        imgUrl: '/assets/blog-card-images/vwap-in-crypto-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'twap-in-crypto-trading',
    loadComponent: () =>
      import('./pages/2025/April/twap-in-crypto-trading/twap-in-crypto-trading.component').then(
        (m) => m.TwapInCryptoTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'twap-in-crypto-trading',
        lastmod: '2025-04-22',
      },
      meta: {
        loc: 'twap-in-crypto-trading',
        fileName: '/twap-in-crypto-trading',
        title: 'TWAP in Crypto Trading',
        description:
          'The article breaks down how the TWAP strategy works in crypto trading, explaining how large orders can be split over time to reduce slippage and market impact. It covers key use cases, benefits, limitations, and platform support, helping traders understand when and how to use TWAP effectively.',
        keywords:
          'twap crypto, time-weighted average price crypto, twap strategy trading, algorithmic trading crypto, large order execution crypto, crypto execution strategies, twap vs vwap, trading without slippage, minimize market impact, institutional crypto trading',
        imgUrl: '/assets/blog-card-images/twap-in-crypto-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'wyckoff-method-in-crypto-trading',
    loadComponent: () =>
      import('./pages/2025/April/wyckoff-method-in-crypto-trading/wyckoff-method-in-crypto-trading.component').then(
        (m) => m.WyckoffMethodInCryptoTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'wyckoff-method-in-crypto-trading',
        lastmod: '2025-04-10',
      },
      meta: {
        loc: 'wyckoff-method-in-crypto-trading',
        fileName: '/wyckoff-method-in-crypto-trading',
        title: 'Wyckoff Method in Crypto Trading',
        description:
          'The article explains how traders apply the Wyckoff Method to crypto markets, identifying accumulation, distribution, and trend shifts based on price and volume structure. It outlines key principles, market phases, and examples using Bitcoin and altcoins to detect institutional activity and improve trade timing in digital asset markets.',
        keywords:
          'wyckoff method crypto, wyckoff trading strategy, accumulation distribution crypto, wyckoff market structure, bitcoin wyckoff pattern, crypto volume analysis, crypto market cycles, institutional trading crypto, smart money crypto trading, wyckoff chart analysis, digital asset trading strategy, price action crypto, market manipulation crypto, wyckoff phases explained, trend reversal crypto',
        imgUrl: '/assets/blog-card-images/wyckoff-method-in-crypto-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'turtle-strategy-in-crypto-markets',
    loadComponent: () =>
      import('./pages/2025/April/turtle-strategy-in-crypto-markets/turtle-strategy-in-crypto-markets.component').then(
        (m) => m.TurtleStrategyInCryptoMarketsComponent,
      ),
    data: {
      sitemap: {
        loc: 'turtle-strategy-in-crypto-markets',
        lastmod: '2025-04-08',
      },
      meta: {
        loc: 'turtle-strategy-in-crypto-markets',
        fileName: '/turtle-strategy-in-crypto-markets',
        title: 'Turtle Strategy in Crypto Markets',
        description:
          'The article explores how the classic Turtle Trading Strategy can be adapted for the cryptocurrency market. It explains the original system\u0027s principles—breakouts, volatility-based sizing, and risk control—and shows how traders are applying it to Bitcoin and altcoins today. The piece includes practical examples, backtesting results, and implementation insights for systematic crypto trading.',
        keywords:
          'turtle trading, turtle strategy crypto, breakout strategy, volatility-based position sizing, average true range, ATR stop-loss, trend-following strategy, crypto trading systems, bitcoin trend strategy, algorithmic crypto trading, backtesting trading strategies, trading bots, crypto strategy optimization, systematic trading crypto, risk management crypto trading, position sizing in crypto, trading strategy execution',
        imgUrl: '/assets/blog-card-images/turtle-strategy-in-crypto-markets.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'ethereum-in-2025',
    loadComponent: () =>
      import('./pages/2025/April/ethereum-in-2025/ethereum-in-2025.component').then((m) => m.EthereumIn2025Component),
    data: {
      sitemap: {
        loc: 'ethereum-in-2025',
        lastmod: '2025-04-03',
      },
      meta: {
        loc: 'ethereum-in-2025',
        fileName: '/ethereum-in-2025',
        title: 'Ethereum in 2025\u2236 Network, Usage, and Upgrades',
        description:
          'The article provides a detailed update on Ethereum as of March 2025, covering its validator architecture, staking trends, rollup usage, protocol upgrades, and institutional adoption across finance, custody, and tokenization.',
        keywords:
          'ethereum 2025, ethereum staking, ethereum rollups, ethereum validators, ethereum protocol upgrade, ethereum blobs, eip-4844, ethereum pectra, ethereum layer 2, ethereum for institutions, tokenized funds ethereum, ethereum custody, ethereum defi, ethereum mainnet, ethereum pos, ethereum snapshot, ethereum usage data',
        imgUrl: '/assets/blog-card-images/ethereum-in-2025.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'a-practical-overview-of-crypto-trading-order-types',
    loadComponent: () =>
      import('./pages/2025/April/a-practical-overview-of-crypto-trading-order-types/a-practical-overview-of-crypto-trading-order-types.component').then(
        (m) => m.APracticalOverviewOfCryptoTradingOrderTypesComponent,
      ),
    data: {
      sitemap: {
        loc: 'a-practical-overview-of-crypto-trading-order-types',
        lastmod: '2025-04-01',
      },
      meta: {
        loc: 'a-practical-overview-of-crypto-trading-order-types',
        fileName: '/a-practical-overview-of-crypto-trading-order-types',
        title: 'A Practical Overview of Crypto Trading Order Types',
        description:
          'The article offers an advanced analysis of all order types in cryptocurrency trading across both spot and derivatives markets, including market, limit, stop-loss, stop-limit, trailing stop, OCO, iceberg, TWAP, VWAP, and others. It explains their mechanics, use cases, associated risks, and implementation differences across centralized (CEX) and decentralized (DEX) platforms. A comparative table highlights execution speed, slippage, and strategy suitability, providing valuable insights for institutional traders, quant developers, and experienced retail participants.',
        keywords:
          'crypto trading order types, market order, limit order, stop-loss, stop-limit, trailing stop, OCO order, iceberg order, TWAP, VWAP, order execution, centralized exchange, decentralized exchange, derivatives trading, algorithmic trading, execution risk, slippage control, time-in-force, post-only, reduce-only, trading strategy, spot vs futures, crypto market microstructure, institutional trading, quantitative trading, advanced trading systems',
        imgUrl: '/assets/blog-card-images/a-practical-overview-of-crypto-trading-order-types.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'multi-agent-deep-q-learning',
    loadComponent: () =>
      import('./pages/2025/March/multi-agent-deep-q-learning/multi-agent-deep-q-learning.component').then(
        (m) => m.MultiAgentDeepQLearningComponent,
      ),
    data: {
      sitemap: {
        loc: 'multi-agent-deep-q-learning',
        lastmod: '2025-03-27',
      },
      meta: {
        loc: 'multi-agent-deep-q-learning',
        fileName: '/multi-agent-deep-q-learning',
        title: 'Multi-Agent Deep Q-Learning for Trading Strategy Optimization',
        description:
          'This article presents a multi-agent Deep Q-Learning framework for developing adaptive trading strategies across multiple cryptocurrencies. By training separate agents with varied decision models on shared historical data, the approach enables improved prediction accuracy, strategy diversification, and dynamic decision-making. Empirical results on assets like BTC, ETH, and XRP show superior performance over traditional buy-and-hold strategies under diverse market conditions, including uptrends, downtrends, and sideways movements.',
        keywords:
          'deep Q-learning, cryptocurrency trading, multi-agent systems, reinforcement learning, algorithmic trading, adaptive trading strategies, crypto market prediction, deep RL agents, trading automation, buy-and-hold comparison, market regimes, trading strategy evaluation, execution modeling, financial AI systems, trading with Q-learning',
        imgUrl: '/assets/blog-card-images/multi-agent-deep-q-learning.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'how-reservation-strategies-shape-optimal-trading',
    loadComponent: () =>
      import('./pages/2025/March/how-reservation-strategies-shape-optimal-trading/how-reservation-strategies-shape-optimal-trading.component').then(
        (m) => m.HowReservationStrategiesShapeOptimalTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-reservation-strategies-shape-optimal-trading',
        lastmod: '2025-03-25',
      },
      meta: {
        loc: 'how-reservation-strategies-shape-optimal-trading',
        fileName: '/how-reservation-strategies-shape-optimal-trading',
        title: 'How Reservation Strategies Shape Optimal Trading',
        description:
          'This article explains how brokers can optimize order execution under uncertainty while following client-imposed reservation strategies like TWAP or IS. Using a utility-maximizing framework and extensions of the Almgren-Chriss model, it shows how to balance market impact, execution risk, and benchmark tracking. Simulations demonstrate improved performance compared to TWAP strategies, offering insights into adaptive, risk-aware execution under real-world conditions.',
        keywords:
          'optimal order execution, reservation strategies, TWAP, IS, execution risk, Almgren-Chriss model, market impact, smart execution, utility-based trading, stochastic execution models, algorithmic trading, trading strategies, execution optimization, adaptive execution, high-frequency trading, institutional trading, financial modeling, quantitative finance',
        imgUrl: '/assets/blog-card-images/how-reservation-strategies-shape-optimal-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'risk-aware-linear-bandits',
    loadComponent: () =>
      import('./pages/2025/March/risk-aware-linear-bandits/risk-aware-linear-bandits.component').then(
        (m) => m.RiskAwareLinearBanditsComponent,
      ),
    data: {
      sitemap: {
        loc: 'risk-aware-linear-bandits',
        lastmod: '2025-03-20',
      },
      meta: {
        loc: 'risk-aware-linear-bandits',
        fileName: '/risk-aware-linear-bandits',
        title: 'Risk-Aware Linear Bandits in Algorithmic Trading',
        description:
          'This article explores the application of risk-aware linear bandits in smart order routing (SOR), focusing on how the RISE and RISE++ algorithms optimize order execution across multiple trading venues. By balancing reward maximization with risk control, these models improve execution quality, reduce transaction costs, and enhance adaptability to changing market conditions. The article also examines empirical validation using NASDAQ ITCH data, demonstrating the effectiveness of risk-aware bandits in real-world trading environments.',
        keywords:
          'risk-aware bandits, smart order routing, algorithmic trading, financial machine learning, execution optimization, trading infrastructure, mean-variance optimization, order execution, market liquidity, risk-aware decision-making, high-frequency trading, multi-armed bandits, adaptive learning, institutional trading, trade execution strategies, order flow, risk-adjusted returns, quantitative finance',
        imgUrl: '/assets/blog-card-images/risk-aware-linear-bandits.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'optimizing-cross-chain-arbitrage',
    loadComponent: () =>
      import('./pages/2025/March/optimizing-cross-chain-arbitrage/optimizing-cross-chain-arbitrage.component').then(
        (m) => m.OptimizingCrossChainArbitrageComponent,
      ),
    data: {
      sitemap: {
        loc: 'optimizing-cross-chain-arbitrage',
        lastmod: '2025-03-18',
      },
      meta: {
        loc: 'optimizing-cross-chain-arbitrage',
        fileName: '/optimizing-cross-chain-arbitrage',
        title: 'Optimizing Cross-Chain Arbitrage in Decentralized Exchanges',
        description:
          'This article explores the concept of cross-chain arbitrage in decentralized exchanges, focusing on the mechanics of arbitrage opportunities between PancakeSwap on the BNB Chain and QuickSwap on Polygon. It examines the impact of transaction costs, liquidity dynamics, and blockchain interoperability, providing insights into the challenges and potential profitability of executing cross-chain arbitrage strategies in decentralized finance.',
        keywords:
          'cross-chain arbitrage, decentralized exchanges, liquidity pools, PancakeSwap, QuickSwap, blockchain interoperability, DeFi arbitrage, transaction costs, decentralized finance, arbitrage opportunities, market efficiency, cross-chain trading, arbitrage strategies, liquidity fragmentation, blockchain price discrepancies, cryptocurrency trading',
        imgUrl: '/assets/blog-card-images/optimizing-cross-chain-arbitrage.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'state-dependent-stochastic-models',
    loadComponent: () =>
      import('./pages/2025/March/state-dependent-stochastic-models/state-dependent-stochastic-models.component').then(
        (m) => m.StateDependentStochasticModelsComponent,
      ),
    data: {
      sitemap: {
        loc: 'state-dependent-stochastic-models',
        lastmod: '2025-03-12',
      },
      meta: {
        loc: 'state-dependent-stochastic-models',
        fileName: '/state-dependent-stochastic-models',
        title: 'State-Dependent Stochastic Models for Fill Probability Estimation',
        description:
          'The article explores the concept of fill probability in limit order books and how state-dependent stochastic models provide more accurate predictions for traders. By considering dynamic factors like liquidity and volatility, these models offer a better understanding of order flow and execution risks, ultimately helping to optimize trading strategies and reduce transaction costs.',
        keywords:
          'fill probability, limit order book, algorithmic trading, execution strategies, stochastic models, market liquidity, high-frequency trading, order flow, trading risk, transaction cost optimization, crypto, cryptocurrency, trading strategies, algotrading',
        imgUrl: '/assets/blog-card-images/state-dependent-stochastic-models.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'axon-trade-partners-with-elven',
    loadComponent: () =>
      import('./pages/2025/March/axon-trade-partners-with-elven/axon-trade-partners-with-elven.component').then(
        (m) => m.AxonTradePartnersWithElvenComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-partners-with-elven',
        lastmod: '2025-03-11',
      },
      meta: {
        loc: 'axon-trade-partners-with-elven',
        fileName: '/axon-trade-partners-with-elven',
        title: 'Axon Trade Partners With Elven',
        description:
          'Elven and Axon Trade are joining forces to improve institutional trading infrastructure. This partnership delivers deep liquidity, low-latency execution, and seamless integration for professional traders. With a focus on stability and performance, the collaboration provides robust connectivity, advanced order routing, and reliable market access across multiple asset classes.',
        keywords:
          'elven, axon trade, institutional trading, algorithmic trading, low-latency execution, deep liquidity, market connectivity, order routing, trading infrastructure, financial technology, professional traders, electronic trading, market access, trading platforms, liquidity providers, high-frequency trading',
        imgUrl: '/assets/blog-card-images/axon-trade-partners-with-elven.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-7',
    loadComponent: () =>
      import('./pages/2025/March/one-hundred-books-for-algorithmic-trading-part-7/one-hundred-books-for-algorithmic-trading-part-7.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart7Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-7',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-7',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-7',
        title: '100 Books for Algorithmic Trading Professionals - Part 7\u2236 Trading Psychology',
        description:
          'This article covers books that explore the psychological aspects of trading, from managing risk and handling stress to recognizing biases that impact financial choices. Topics include discipline, mental resilience, and strategies for maintaining focus in volatile markets. Traders will find insights from psychology, neuroscience, and behavioral finance to improve performance and build confidence.',
        keywords:
          'trading psychology, trader mindset, cognitive biases, behavioral finance, emotional resilience, risk management, trading discipline, decision-making in trading, market psychology, trading performance, self-discipline, stress management in trading, psychology of investing, financial decision-making, mental strategies for traders, neuroscience of trading, trading behavior, psychology of risk',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-7.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-6',
    loadComponent: () =>
      import('./pages/2025/March/one-hundred-books-for-algorithmic-trading-part-6/one-hundred-books-for-algorithmic-trading-part-6.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart6Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-6',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-6',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-6',
        title: '100 Books for Algorithmic Trading Professionals - Part 6\u2236 Case Studies and Real-World Stories',
        description:
          'History shapes modern trading. This article presents a selection of books that explore real-world case studies of algorithmic trading, financial crises, and market manipulation. From high-frequency trading to the rise of crypto, these books reveal the decisions and events that redefined global finance. Traders, analysts, and investors will gain insights into how markets evolve, the impact of hedge funds, and the role of technology in reshaping financial systems.',
        keywords:
          'algorithmic trading, financial markets, high-frequency trading, hedge funds, market manipulation, financial history, flash crash, crypto markets, decentralized finance, economic crises, hedge fund strategies, market speculation, algorithmic traders, trading technology, financial bubbles, Wall Street stories, institutional trading, risk management, market structure, financial regulation',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-6.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-5',
    loadComponent: () =>
      import('./pages/2025/March/one-hundred-books-for-algorithmic-trading-part-5/one-hundred-books-for-algorithmic-trading-part-5.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart5Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-5',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-5',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-5',
        title:
          '100 Books for Algorithmic Trading Professionals - Part 5\u2236 Portfolio Optimization and Risk Management',
        description:
          'Effective portfolio optimization and risk management are essential for long-term trading success. This article explores books covering asset allocation, risk forecasting, stress testing, and quantitative techniques like Bayesian estimation and machine learning. From classical portfolio theory to modern risk modeling, these resources provide practical insights for traders, asset managers, and financial professionals looking to build resilient portfolios and navigate market uncertainty',
        keywords:
          'portfolio optimization, risk management, asset allocation, financial modeling, quantitative finance, Bayesian estimation, machine learning in trading, stress testing, value-at-risk, Monte Carlo simulation, hedge funds, investment strategies, portfolio construction, market risk, portfolio theory, risk forecasting, algorithmic trading, financial risk analysis, Black-Litterman model, asset management, volatility analysis',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-5.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-4',
    loadComponent: () =>
      import('./pages/2025/February/one-hundred-books-for-algorithmic-trading-part-4/one-hundred-books-for-algorithmic-trading-part-4.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart4Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-4',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-4',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-4',
        title: '100 Books for Algorithmic Trading Professionals - Part 4\u2236  AI and Machine Learning in Trading',
        description:
          'Machine learning is transforming trading by uncovering patterns, optimizing execution, and managing risk at scale. This selection of books covers regression models, neural networks, reinforcement learning, and AI-powered risk assessment. Readers will explore high-frequency trading, statistical arbitrage, and market simulation, gaining insights into structuring data pipelines and refining predictive models.',
        keywords:
          'algorithmic trading, AI in trading, machine learning in finance, quantitative trading, deep learning for trading, reinforcement learning, financial modeling, high-frequency trading, statistical arbitrage, risk management, trading algorithms, market data analysis, predictive analytics, AI trading systems, algorithmic execution, portfolio optimization, automated trading',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-4.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-3',
    loadComponent: () =>
      import('./pages/2025/February/one-hundred-books-for-algorithmic-trading-part-3/one-hundred-books-for-algorithmic-trading-part-3.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart3Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-3',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-3',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-3',
        title: '100 Books for Algorithmic Trading Professionals - Part 3\u2236  Coding and Tools for Trading',
        description:
          'Efficient trading algorithms rely on well-structured code. This selection of books covers Python, R, Java, and C++ for algorithmic trading, including topics like backtesting frameworks, data pipelines, risk modeling, and API integrations. From optimizing execution speed to automating research, these resources help traders and developers build scalable financial systems.',
        keywords:
          'algorithmic trading, algo trading, quant trading, trading algorithms, financial programming, Python for trading, R for trading, Java for trading, C++ trading systems, backtesting, financial modeling, quantitative finance, high-frequency trading, systematic trading, risk management, trading software, API integrations, market data processing, trading automation',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-3.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-2',
    loadComponent: () =>
      import('./pages/2025/February/one-hundred-books-for-algorithmic-trading-part-2/one-hundred-books-for-algorithmic-trading-part-2.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart2Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-2',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-2',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-2',
        title: '100 Books for Algorithmic Trading Professionals - Part 2\u2236  Math & Statistics for Trading',
        description:
          'Mathematics is the foundation of algorithmic trading. From probability theory to stochastic calculus, a solid grasp of mathematical concepts allows traders to analyze data, measure risk, and refine their strategies. This selection of books covers essential topics, including financial modeling, statistical arbitrage, and quantitative methods used in modern trading. ',
        keywords:
          'algorithmic trading, algotrading, auant trading, financial mathematics, probability theory, stochastic calculus, risk management, market microstructure, statistical arbitrage, quantitative finance, data-driven trading, trading models, financial engineering, quantitative strategies, high-frequency trading, systematic trading, financial markets, trading systems',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-2.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'one-hundred-books-for-algorithmic-trading-part-1',
    loadComponent: () =>
      import('./pages/2025/February/one-hundred-books-for-algorithmic-trading-part-1/one-hundred-books-for-algorithmic-trading-part-1.component').then(
        (m) => m.OneHundredBooksForAlgorithmicTradingPart1Component,
      ),
    data: {
      sitemap: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-1',
        lastmod: '2025-03-24',
      },
      meta: {
        loc: 'one-hundred-books-for-algorithmic-trading-part-1',
        fileName: '/one-hundred-books-for-algorithmic-trading-part-1',
        title: '100 Books for Algorithmic Trading Professionals - Part 1\u2236 Foundational Concepts',
        description:
          'This article lists essential books on algorithmic trading, covering market structure, trading strategies, risk management, and system development. It includes foundational works on quantitative models, high-frequency trading, and portfolio management, offering key insights for traders and researchers.',
        keywords:
          'algorithmic trading books, algorithmic trading, quantitative trading, trading strategies, market structure, risk management, backtesting, portfolio management, high-frequency trading, systematic trading, financial markets, trading systems',
        imgUrl: '/assets/blog-card-images/one-hundred-books-for-algorithmic-trading-part-1.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'improving-cryptocurrency-predictions-with-rnn-models',
    loadComponent: () =>
      import('./pages/2025/February/improving-cryptocurrency-predictions-with-rnn-models/improving-cryptocurrency-predictions-with-rnn-models.component').then(
        (m) => m.ImprovingCryptocurrencyPredictionsWithRnnModelsComponent,
      ),
    data: {
      sitemap: {
        loc: 'improving-cryptocurrency-predictions-with-rnn-models',
        lastmod: '2025-02-12',
      },
      meta: {
        loc: 'improving-cryptocurrency-predictions-with-rnn-models',
        fileName: '/improving-cryptocurrency-predictions-with-rnn-models',
        title: 'Improving Cryptocurrency Predictions with RNN Models',
        description:
          'Explore how advanced RNN models, including LSTM, GRU, and Bi-LSTM, are transforming cryptocurrency price forecasting. This article examines how deep learning techniques enhance trading strategies with improved accuracy and actionable insights for traders and analysts.',
        keywords:
          'cryptocurrency predictions, RNN models, LSTM, GRU, Bi-LSTM, price forecasting, trading strategies, deep learning in finance, real-time cryptocurrency trading, financial time series, optimized trading models',
        imgUrl: '/assets/blog-card-images/improving-cryptocurrency-predictions-with-rnn-models.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'profitable-arbitrage-with-dynamic-cointegration',
    loadComponent: () =>
      import('./pages/2025/February/profitable-arbitrage-with-dynamic-cointegration/profitable-arbitrage-with-dynamic-cointegration.component').then(
        (m) => m.ProfitableArbitrageWithDynamicCointegrationComponent,
      ),
    data: {
      sitemap: {
        loc: 'profitable-arbitrage-with-dynamic-cointegration',
        lastmod: '2025-02-10',
      },
      meta: {
        loc: 'profitable-arbitrage-with-dynamic-cointegration',
        fileName: '/profitable-arbitrage-with-dynamic-cointegration',
        title: 'Profitable Arbitrage with Dynamic Cointegration in Cryptocurrencies',
        description:
          'An in-depth exploration of dynamic cointegration-based pairs trading in cryptocurrency markets, based on research by Masood Tadi and Irina Kortchemski. The article covers advanced statistical models, profitability insights, and practical applications, with a focus on how Axon Trade supports traders in navigating dynamic markets.',
        keywords:
          'crypto arbitrage, dynamic cointegration, pairs trading strategy, cryptocurrency trading, statistical arbitrage, cointegration in crypto, crypto trading models, profitability in crypto, advanced trading strategies, axon trade blog, risk management in trading, crypto market insights, dynamic trading strategies, cointegration tests, arbitrage opportunities crypto, basket trading crypto, crypto trading, cryptocurrency',
        imgUrl: '/assets/blog-card-images/profitable-arbitrage-with-dynamic-cointegration.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'the-rise-of-ai-tokens',
    loadComponent: () =>
      import('./pages/2025/February/the-rise-of-ai-tokens/the-rise-of-ai-tokens.component').then(
        (m) => m.TheRiseOfAiTokensComponent,
      ),
    data: {
      sitemap: {
        loc: 'the-rise-of-ai-tokens',
        lastmod: '2025-02-06',
      },
      meta: {
        loc: 'the-rise-of-ai-tokens',
        fileName: '/the-rise-of-ai-tokens',
        title: 'The Rise of AI Tokens and Their Impact on Digital Asset Trading',
        description:
          'AI tokens are reshaping blockchain ecosystems by integrating artificial intelligence with decentralized technologies. This article explores their applications, market trends, challenges, and how Axon Trade provides advanced tools to support traders in navigating the evolving digital asset landscape.',
        keywords:
          'crypto, cryptocurrency, crypto AI, AI tokens, Artificial intelligence tokens, Blockchain, AI-powered platforms, Decentralized marketplaces, Digital asset trading, Tokenized data marketplaces, Algorithmic trading tools, AI in DeFi, AI-driven trading strategies, Axon Trade, FIX API for trading, Real-time market data, Digital asset management, trading tools, AI token market, trading history, crypto trading',
        imgUrl: '/assets/blog-card-images/the-rise-of-ai-tokens.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'solana-and-sol-market',
    loadComponent: () =>
      import('./pages/2025/February/solana-and-sol-market/solana-and-sol-market.component').then(
        (m) => m.SolanaAndSolMarketComponent,
      ),
    data: {
      sitemap: {
        loc: 'solana-and-sol-market',
        lastmod: '2025-02-04',
      },
      meta: {
        loc: 'solana-and-sol-market',
        fileName: '/solana-and-sol-market',
        title: 'Solana and SOL Market',
        description:
          'This article explores Solana\u0027s blockchain, focusing on its unique Proof of History mechanism and scalable architecture. It compares Solana with Bitcoin and Ethereum, providing insight into its increasing adoption among institutional investors. By examining Solana\u0027s tokenomics and market structure, the piece highlights the network\u0027s potential to address the scalability challenges faced by other blockchains, particularly in decentralized finance (DeFi) and digital assets.',
        keywords:
          'solana, solana blockchain, proof of history, solana tokenomics, decentralized finance, defi, solana scalability, solana vs bitcoin, solana vs ethereum, solana market, solana institutional, solana future, digital assets, solana speed, solana token, decentralized applications, solana transaction fees, solana ecosystem, solana network, solana financial applications, solana cryptocurrency, solana blockchain technology, solana defi, solana nft, solana growth, blockchain scalability, crypto market, solana adoption',
        imgUrl: '/assets/blog-card-images/solana-and-sol-market.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'market-volatility-and-its-implications-for-traders',
    loadComponent: () =>
      import('./pages/2025/January/market-volatility-and-its-implications-for-traders/market-volatility-and-its-implications-for-traders.component').then(
        (m) => m.MarketVolatilityAndItsImplicationsForTradersComponent,
      ),
    data: {
      sitemap: {
        loc: 'market-volatility-and-its-implications-for-traders',
        lastmod: '2025-01-30',
      },
      meta: {
        loc: 'market-volatility-and-its-implications-for-traders',
        fileName: '/market-volatility-and-its-implications-for-traders',
        title: 'Market Volatility and Its Implications for Traders',
        description:
          'This article delves into the impact of market volatility on cryptocurrency trading, highlighting historical price trends of Bitcoin and altcoins, key volatility drivers, and technical indicators like RSI, Bollinger Bands, and Moving Averages. It also outlines strategies for identifying overbought and oversold conditions and managing risk effectively. Based on Kristian Ratia\u0027s research, the article provides valuable insights for traders aiming to navigate unpredictable market swings.',
        keywords:
          'crypto market volatility, Bitcoin price trends, altcoin volatility, cryptocurrency trading strategies, RSI indicator, Bollinger Bands crypto, moving averages crypto, overbought vs oversold, crypto risk management, crypto price swings, technical indicators, crypto trading tips, volatile markets, institutional crypto trading, retail crypto trading',
        imgUrl: '/assets/blog-card-images/market-volatility-and-its-implications-for-traders.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'advanced-algorithmic-trading-with-reinforcement-learning',
    loadComponent: () =>
      import('./pages/2025/January/advanced-algorithmic-trading-with-reinforcement-learning/advanced-algorithmic-trading-with-reinforcement-learning.component').then(
        (m) => m.AdvancedAlgorithmicTradingWithReinforcementLearningComponent,
      ),
    data: {
      sitemap: {
        loc: 'advanced-algorithmic-trading-with-reinforcement-learning',
        lastmod: '2025-01-28',
      },
      meta: {
        loc: 'advanced-algorithmic-trading-with-reinforcement-learning',
        fileName: '/advanced-algorithmic-trading-with-reinforcement-learning',
        title: 'Advanced Algorithmic Trading with Reinforcement Learning',
        description:
          'Deep Reinforcement Learning (DRL) methods, particularly Deep Q-Networks (DQNs), have emerged as critical components in designing adaptive trading systems. This article explores the advancements in DQN-based trading algorithms, highlighting their performance improvements when augmented with advanced techniques such as Double DQN, Dueling DQN, Prioritized Experience Replay (PER), and Noisy Networks.',
        keywords:
          'algorithmic trading, reinforcement learning, prioritized experience replay, automated trading systems, cryptocurrency trading, portfolio optimization, machine learning in finance, stock trading algorithms, and high-frequency AI trading systems',
        imgUrl: '/assets/blog-card-images/advanced-algorithmic-trading-with-reinforcement-learning.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'how-blockchain-metrics-improve-cryptocurrency-trading-strategies',
    loadComponent: () =>
      import('./pages/2025/January/how-blockchain-metrics-improve-cryptocurrency-trading-strategies/how-blockchain-metrics-improve-cryptocurrency-trading-strategies.component').then(
        (m) => m.HowBlockchainMetricsImproveCryptocurrencyTradingStrategiesComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-blockchain-metrics-improve-cryptocurrency-trading-strategies',
        lastmod: '2025-01-22',
      },
      meta: {
        loc: 'how-blockchain-metrics-improve-cryptocurrency-trading-strategies',
        fileName: '/how-blockchain-metrics-improve-cryptocurrency-trading-strategies',
        title: 'How Blockchain Metrics Improve Cryptocurrency Trading Strategies',
        description:
          'Learn how blockchain metrics like hash rate, transaction costs, and wallet growth offer a data-driven approach to cryptocurrency trading. This article explores actionable strategies, highlighting how traders can integrate these insights for better decision-making and market analysis.',
        keywords:
          'cryptocurrency trading, blockchain metrics, hash rate, mining difficulty, transaction costs, wallet growth, trading strategies, data-driven trading, blockchain analysis, cryptocurrency tools, trading insights',
        imgUrl: '/assets/blog-card-images/how-blockchain-metrics-improve-cryptocurrency-trading-strategies.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'the-impact-of-fees-on-cryptocurrency-trading',
    loadComponent: () =>
      import('./pages/the-impact-of-fees-on-cryptocurrency-trading/the-impact-of-fees-on-cryptocurrency-trading.component').then(
        (m) => m.TheImpactOfFeesOnCryptocurrencyTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'the-impact-of-fees-on-cryptocurrency-trading',
        lastmod: '2025-01-20',
      },
      meta: {
        loc: 'the-impact-of-fees-on-cryptocurrency-trading',
        fileName: '/the-impact-of-fees-on-cryptocurrency-trading',
        title: 'The Impact of Fees on Cryptocurrency Trading',
        description:
          'Cryptocurrency traders often overlook the significant impact of transaction fees on their overall profitability. This in-depth article explores how trading fees, including maker and taker fees, withdrawal costs, and network charges, can substantially influence trading outcomes. Drawing from Kristian Ratia\u0027s research, it provides a detailed analysis of how high-frequency strategies and technical indicators like Bollinger Bands, Moving Averages, and RSI interact with fee structures. The article also offers practical strategies to minimize costs and highlights the importance of fee-conscious trading for both institutional and retail participants.',
        keywords:
          'cryptocurrency trading fees, maker fees, taker fees, crypto withdrawal, network transaction fees, high-frequency crypto trading, low-frequency trading strategies, crypto trading profitability, Bollinger Bands analysis, SMA vs EMA in crypto, crypto market trends 2025, technical analysis in crypto trading, crypto trading strategies 2025, fee-conscious trading tips, cryptocurrency transaction costs, crypto exchange fees comparison, algorithmic crypto trading, institutional crypto trading strategies, retail crypto trading tips, crypto market volatility strategies',
        imgUrl: '/assets/blog-card-images/the-impact-of-fees-on-cryptocurrency-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'from-prediction-to-profit',
    loadComponent: () =>
      import('./pages/from-prediction-to-profit/from-prediction-to-profit.component').then(
        (m) => m.FromPredictionToProfitComponent,
      ),
    data: {
      sitemap: {
        loc: 'from-prediction-to-profit',
        lastmod: '2025-01-16',
      },
      meta: {
        loc: 'from-prediction-to-profit',
        fileName: '/from-prediction-to-profit',
        title: 'From Prediction to Profit\u2236 How Price Forecasting Drives Cryptocurrency Trading Strategies',
        description:
          'Cryptocurrency trading thrives on the ability to interpret market fluctuations and anticipate price trends. This article is based on the review titled From Prediction to Profit\u2236 A Comprehensive Review of Cryptocurrency Trading Strategies and Price Forecasting Techniques by Sattarov Otabek and Jaeyoung Choi (IEEE Access, 2024), which addresses the gap between price predictions and trading success. ',
        keywords:
          'cryptocurrency trading, price prediction, crypto, Bitcoin, Ethereum, Litecoin, trading models, machine learning, sentiment analysis, algorithmic trading, technical analysis, economic models, statistical models, BTC, ETH, LTC, Sharpe Ratio, trading strategies, market data',
        imgUrl: '/assets/blog-card-images/from-prediction-to-profit.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'role-of-deep-learning-in-modern-finance',
    loadComponent: () =>
      import('./pages/role-of-deep-learning-in-modern-finance/role-of-deep-learning-in-modern-finance.component').then(
        (m) => m.RoleOfDeepLearningInModernFinanceComponent,
      ),
    data: {
      sitemap: {
        loc: 'role-of-deep-learning-in-modern-finance',
        lastmod: '2025-01-14',
      },
      meta: {
        loc: 'role-of-deep-learning-in-modern-finance',
        fileName: '/role-of-deep-learning-in-modern-finance',
        title: 'The Role of Deep Learning in Modern Finance',
        description:
          'An in-depth look at the integration of deep learning models such as RNNs, CNNs, and Transformers in finance. Learn how Explainable AI tools like SHAP and LIME ensure transparency in credit scoring, while GANs augment risk analysis and market scenario testing for portfolio optimization.',
        keywords:
          'crypto, crypto trading, algorithmic trading, portfolio manager, risk management, market data, automated trading, AI, ML, deep learning, AI in trading, portfolio management',
        imgUrl: '/assets/blog-card-images/role-of-deep-learning-in-modern-finance.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends',
    loadComponent: () =>
      import('./pages/crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends/crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends.component').then(
        (m) => m.CryptoHedgeFundsIn2025KeyForecastsAndEmergingTrendsComponent,
      ),
    data: {
      sitemap: {
        loc: 'crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends',
        lastmod: '2025-01-08',
      },
      meta: {
        loc: 'crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends',
        fileName: '/crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends',
        title: 'Crypto Hedge Funds in 2025\u2236 Key Forecasts and Emerging Trends',
        description:
          'Crypto hedge funds continue growing and adapting as they face new opportunities and challenges. As 2025 unfolds, this sector is expected to change significantly, influenced by regulations, technological progress, and shifting global markets. This article outlines key trends for the year and examines how these factors may shape institutional trading strategies.',
        keywords:
          'crypto hedge funds, digital asset hedge funds, institutional investment trends, derivatives trading in crypto, tokenization, spot Bitcoin ETFs, hedge fund strategies, crypto market, algo trading, hft, high-frequency trading',
        imgUrl: '/assets/blog-card-images/crypto-hedge-funds-in-2025-key-forecasts-and-emerging-trends.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'advanced-order-routing-techniques-for-multi-exchange-trading',
    loadComponent: () =>
      import('./pages/advanced-order-routing-techniques-for-multi-exchange-trading/advanced-order-routing-techniques-for-multi-exchange-trading.component').then(
        (m) => m.AdvancedOrderRoutingTechniquesForMultiExchangeTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'advanced-order-routing-techniques-for-multi-exchange-trading',
        lastmod: '2025-01-06',
      },
      meta: {
        loc: 'advanced-order-routing-techniques-for-multi-exchange-trading',
        fileName: '/advanced-order-routing-techniques-for-multi-exchange-trading',
        title: 'Advanced Order Routing Techniques for Multi-Exchange Trading',
        description:
          'In digital asset markets, executing orders across various exchanges effectively can lead to better prices, lower costs, and broader liquidity access. Advanced Order Routing (AOR) methods have become essential for traders and institutions operating in fragmented markets. By applying algorithms and data-driven methods, AOR solutions make order management more precise and reliable.',
        keywords:
          'crypto, crypto trading, Advanced Order Routing, AOR, Smart Order Routers, SOR, liquidity aggregation, algorithmic execution, DeFi routing, crypto trading solutions, order management, digital asset execution, trading technology, decentralized exchanges, centralized exchanges, portfolio rebalancing, high-frequency trading',
        imgUrl: '/assets/blog-card-images/advanced-order-routing-techniques-for-multi-exchange-trading.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'crypto-hedge-fund-industry-retrospective-2024',
    loadComponent: () =>
      import('./pages/crypto-hedge-fund-industry-retrospective-2024/crypto-hedge-fund-industry-retrospective-2024.component').then(
        (m) => m.CryptoHedgeFundIndustryRetrospective2024Component,
      ),
    data: {
      sitemap: {
        loc: 'crypto-hedge-fund-industry-retrospective-2024',
        lastmod: '2025-01-02',
      },
      meta: {
        loc: 'crypto-hedge-fund-industry-retrospective-2024',
        fileName: '/crypto-hedge-fund-industry-retrospective-2024',
        title: 'Crypto Hedge Fund Industry Retrospective\u2236 2024 Highlights',
        description:
          'A detailed examination of the crypto hedge fund industry in 2024, highlighting trends such as institutional adoption, the rise of derivatives trading, and the growing role of tokenization. The article explores differences in strategies, regulatory progress, and challenges faced by hedge funds operating in digital asset markets.',
        keywords:
          'crypto hedge funds, digital asset hedge funds, institutional investment trends, derivatives trading in crypto, tokenization, spot Bitcoin ETFs, hedge fund strategies, crypto market, algo trading, hft, high-frequency trading',
        imgUrl: '/assets/blog-card-images/crypto-hedge-fund-industry-retrospective-2024.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'axon-trade-and-whitebits-strategic-partnership',
    loadComponent: () =>
      import('./pages/axon-trade-and-whitebits-strategic-partnership/axon-trade-and-whitebits-strategic-partnership.component').then(
        (m) => m.AxonTradeAndWhitebitsStrategicPartnershipComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-and-whitebits-strategic-partnership',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-and-whitebits-strategic-partnership',
        fileName: '/axon-trade-and-whitebits-strategic-partnership',
        title: 'Axon Trade and WhiteBIT Strategic Partnership',
        description:
          'Axon Trade partners with WhiteBIT to enhance the crypto trading experience, offering FIX API access, real-time data, and low-latency execution',
        keywords:
          'axon trade, whitebit, cryptocurrency exchange, fix api, market data, low-latency execution, institutional trading, crypto trading, high-frequency trading, hft, algotrading, crypto',
        imgUrl: '/assets/blog-card-images/axon-trade-and-whitebits-strategic-partnership.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer',
    loadComponent: () =>
      import('./pages/gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer/gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer.component').then(
        (m) => m.GateIoAndAxonTradeStrategicCollaborationAndExclusiveOfferComponent,
      ),
    data: {
      sitemap: {
        loc: 'gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer',
        fileName: '/gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer',
        title: 'Gate.io and Axon Trade\u0027s Strategic Collaboration. and Exclusive Offer',
        description:
          'Gate.io partners with Axon Trade to enhance trading for professionals and institutions, offering FIX API, low-latency execution, and a 3-month zero-fee promotion with a free VPS',
        keywords:
          'Gate.io, cryptocurrency exchange, crypto trading, EMS, FIX API, real-time market data, order management, futures trading, margin trading, digital assets, VPS, HFT',
        imgUrl: '/assets/blog-card-images/gate-io-and-axon-trade-strategic-collaboration-and-exclusive-offer.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'axon-trade-and-gate-io-trading-campaign',
    loadComponent: () =>
      import('./pages/axon-trade-and-gate-io-trading-campaign/axon-trade-and-gate-io-trading-campaign.component').then(
        (m) => m.AxonTradeAndGateIoTradingCampaignComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-and-gate-io-trading-campaign',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-and-gate-io-trading-campaign',
        fileName: '/axon-trade-and-gate-io-trading-campaign',
        title: 'Axon Trade x Gate.io Trading Campaign',
        description:
          'The Axon Trade and Gate.io Trading Campaign is already underway, offering users the chance to gain VIP5 status on Gate.io and earn up to $15,000 USDT in rewards.',
        keywords:
          'Axon Trade, Gate.io, VIP5, cryptocurrency exchange, crypto trading, FIX API, real-time market data, spot trading, futures trading, margin trading, digital assets, trading rewards, USDT',
        imgUrl: '/assets/blog-card-images/axon-trade-and-gate-io-trading-campaign.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'bitmart-partners-with-axon-trade',
    loadComponent: () =>
      import('./pages/bitmart-partners-with-axon-trade/bitmart-partners-with-axon-trade.component').then(
        (m) => m.BitmartPartnersWithAxonTradeComponent,
      ),
    data: {
      sitemap: {
        loc: 'bitmart-partners-with-axon-trade',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'bitmart-partners-with-axon-trade',
        fileName: '/bitmart-partners-with-axon-trade',
        title: 'Axon Trade Partners with BitMart. Exclusive Offer and Advanced Trading Solutions',
        description:
          'Axon Trade partners with BitMart to offer free trading for three months, reduced fees, and a free VPS. Discover advanced trading tools, including EMS integration with real-time data and order management.',
        keywords:
          'BitMart, cryptocurrency exchange, crypto trading, EMS, FIX API, real-time market data, order management, staking, futures trading, margin trading, digital assets, VPS, HFT',
        imgUrl: '/assets/blog-card-images/bitmart-partners-with-axon-trade.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'optimization-techniques-for-fix-based-trading-applications',
    loadComponent: () =>
      import('./pages/optimization-techniques-for-fix-based-trading-applications/optimization-techniques-for-fix-based-trading-applications.component').then(
        (m) => m.OptimizationTechniquesForFixBasedTradingApplicationsComponent,
      ),
    data: {
      sitemap: {
        loc: 'optimization-techniques-for-fix-based-trading-applications',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'optimization-techniques-for-fix-based-trading-applications',
        fileName: '/optimization-techniques-for-fix-based-trading-applications',
        title: 'Optimization techniques for FIX-based trading applications | Axon Trade',
        description: 'The article discusses optimization techniques for FIX-based trading applications',
        keywords:
          'fix api,market data,real time market data,fix api crypto,binance fix api,fix api for crypto trading,fix api for digital asset trading,industry',
        imgUrl: '/assets/blog-card-images/axon-tarde-optimization-techniques-for-FIX-based-trading-applications.png',
        og_type: 'article',
      },
    },
  },
  {
    path: 'how-to-build-your-next-trading-system-using-axon-trade-oems',
    loadComponent: () =>
      import('./pages/how-to-build-your-next-trading-system-using-axon-trade-oems/how-to-build-your-next-trading-system-using-axon-trade-oems.component').then(
        (m) => m.HowToBuildYourNextTradingSystemUsingAxonTradeOemsComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-to-build-your-next-trading-system-using-axon-trade-oems',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-to-build-your-next-trading-system-using-axon-trade-oems',
        fileName: '/how-to-build-your-next-trading-system-using-axon-trade-oems',
        title: 'How to build your next trading system using Axon Trade OEMS | Axon Trade',
        description:
          'We are pleased to invite you to our upcoming webinar How to build your next trading system using Axon Trade OEMS. We will guide you through the extensive functionality of our FIX API for trading and market data, as well as provide an in-depth explanation on how to seamlessly integrate with our Management REST API. We look forward to sharing our knowledge and insights with you.',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-tarde-how-to-build-your-next-trading-system-using-Axon-Trade-OEMS.png',
      },
    },
  },
  {
    path: 'axon-trade-oems-is-now-available-in-equinix-hk2',
    loadComponent: () =>
      import('./pages/axon-trade-oems-is-now-available-in-equinix-hk2/axon-trade-oems-is-now-available-in-equinix-hk2.component').then(
        (m) => m.AxonTradeOemsIsNowAvailableInEquinixHk2Component,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-oems-is-now-available-in-equinix-hk2',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-oems-is-now-available-in-equinix-hk2',
        fileName: '/axon-trade-oems-is-now-available-in-equinix-hk2',
        title: 'Axon Trade OEMS is Now Available in Equinix HK2 | | Axon Trade',
        description:
          'We are happy to announce that Axon Trade\u0027s OEMS for digital asset trading is now available in the Equinix HK2 location – Hong Kong! We have connectors with 30+ exchanges, including these ones\u2236 BinanceBybitOKXHuobi Global',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-OEMS-New-Point-of-Presence-equinix-hk2.png',
      },
    },
  },
  {
    path: 'api-updates-january-2023',
    loadComponent: () =>
      import('./pages/api-updates-january-2023/api-updates-january-2023.component').then(
        (m) => m.ApiUpdatesJanuary2023Component,
      ),
    data: {
      sitemap: {
        loc: 'api-updates-january-2023',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'api-updates-january-2023',
        fileName: '/api-updates-january-2023',
        title: 'XTRD | FIX API | Updates | Axon Trade',
        description:
          'January 2023 FIX API updates - universal Order Cancel/Replace Request and ability to fetch exchange statuses using TradingSessionStatusRequest message',
        keywords:
          'crypto oems, crypto fix api, fix api, crypto exchange fix api, crypto trading, algotrading, crypto hft, high frequency trading crypto',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-API-updates-january-2023.png',
      },
    },
  },
  {
    path: 'bjf-trading-group-partners-with-axon-trade',
    loadComponent: () =>
      import('./pages/bjf-trading-group-partners-with-axon-trade/bjf-trading-group-partners-with-axon-trade.component').then(
        (m) => m.BjfTradingGroupPartnersWithAxonTradeComponent,
      ),
    data: {
      sitemap: {
        loc: 'bjf-trading-group-partners-with-axon-trade',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'bjf-trading-group-partners-with-axon-trade',
        fileName: '/bjf-trading-group-partners-with-axon-trade',
        title: 'BJF Trading Group Partners With Axon Trade | | Axon Trade',
        description:
          'FX Software development company BJF Trading Group, announces today its partnership with Axon Trade, OEMS for digital asset trading. This partnership will allow customers to use sophisticated trading algorithms created by BJF Trading Group, across the entire spectrum of cryptocurrency exchanges supported by the Axon Trade OEMS. &quot;Digital asset space is still full of different inefficiencies that might be exploited to gain a certain profit. Of course, there is no free lunch, and to get positive results, you must be properly equipped and educated. BJF Trading Group did tremendous work discovering small operational nuances of traditional and crypto markets and turning them into algos.Axon Trade provides you with the tools that you can use to &quot;test the digital water&quot;, said Serg Gulko, Co-Founder and CEO at Axon Trade. Founded in 2000, BJF Trading group is a boutique Forex and Cryptocurrencies software development company that provides solutions for FX trading, specializing in Expert',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-bjf-trading-group.png',
      },
    },
  },
  {
    path: 'lessons-after-ftx-collapse',
    loadComponent: () =>
      import('./pages/lessons-after-ftx-collapse/lessons-after-ftx-collapse.component').then(
        (m) => m.LessonsAfterFtxCollapseComponent,
      ),
    data: {
      sitemap: {
        loc: 'lessons-after-ftx-collapse',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'lessons-after-ftx-collapse',
        fileName: '/lessons-after-ftx-collapse',
        title: 'Lessons after FTX collapse | | Axon Trade',
        description:
          'By Axon Trade\u0027s CEO Serg Gulko With FTX collapse, you might notice a growing number of voices that DEFI is a solution. It is not. You\u0027ll replace one risk with another. A balanced approach - that\u0027s the answer. Never put all eggs into a single basket.Rule of thumb on reducing risks with crypto - check for interconnections. By trading on FTX using the money you borrow on BlockFi you expose yourself to potential chain effects.If you don\u0027t have the time/money/willpower to do proper due diligence, one of the solutions is to use brokers (I never thought I\u0027d say something like that...) instead of direct market access. But if you do have time, resources, and willpower to invest in robust trading infrastructure that helps you to diversify and manage risks - consider using Axon Trade OEMS. Do not lock yourself with other FTXs.',
        keywords: 'industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/lessons-after-FTX-collapce.png',
      },
    },
  },
  {
    path: 'latency-arbitrage-on-crypto-markets-using-axon-trade-oems',
    loadComponent: () =>
      import('./pages/latency-arbitrage-on-crypto-markets-using-axon-trade-oems/latency-arbitrage-on-crypto-markets-using-axon-trade-oems.component').then(
        (m) => m.LatencyArbitrageOnCryptoMarketsUsingAxonTradeOemsComponent,
      ),
    data: {
      sitemap: {
        loc: 'latency-arbitrage-on-crypto-markets-using-axon-trade-oems',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'latency-arbitrage-on-crypto-markets-using-axon-trade-oems',
        fileName: '/latency-arbitrage-on-crypto-markets-using-axon-trade-oems',
        title: 'Latency arbitrage on crypto markets using Axon Trade OEMS | Axon Trade',
        description:
          'The article describes one of Axon Trade\u0027s side projects - a latency arbitrage trading system.',
        keywords: 'crypto latency arb,crypto latency arbitrage,latency arb trading,industry,low-latency',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/latency-arbitrage-on-crypto-markets-using-axon-trade-oems.png',
      },
    },
  },
  {
    path: 'axon-trade-to-integrate-with-brokeree-solutions',
    loadComponent: () =>
      import('./pages/axon-trade-to-integrate-with-brokeree-solutions/axon-trade-to-integrate-with-brokeree-solutions.component').then(
        (m) => m.AxonTradeToIntegrateWithBrokereeSolutionsComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-to-integrate-with-brokeree-solutions',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-to-integrate-with-brokeree-solutions',
        fileName: '/axon-trade-to-integrate-with-brokeree-solutions',
        title: 'MT4/MT5 Crypto Bridge by Brokeree | Axon Trade',
        description:
          'Brokeree Solutions, a turnkey solutions developer for multi-asset brokers, has just announced the integration of its flagship Liquidity Bridge with Axon Trade – orders and execution management system (OEMS) for digital asset trading with a strong focus on low-latency and high-throughput execution. Multi-server Liquidity Bridge is a comprehensive solution that combines advanced A/B book order execution, smart liquidity aggregation, and risk management. With this integration, brokers may introduce new crypto instruments for their clients on MetaTrader 4 and 5 platforms via the market data and liquidity provided by Axon Trade. &quot;We provide access to major US, Asia, and Europe exchanges. By leveraging our stack, clients can easily increase their liquidity offering by adding new LPs from the management panel without spending weeks for integration followed by neverending maintenance,&quot; said Serg Gulko, CEO of Axon Trade. &quot;I came to the digital asset space from FX and am familiar with the',
        keywords: 'mt4 crypto,mt5 crypto,metaquotes crypto,mt4 bridge for crypto',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-brokeree.png',
      },
    },
  },
  {
    path: 'kucoin-is-now-available-through-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/kucoin-is-now-available-through-axon-trade-fix-api/kucoin-is-now-available-through-axon-trade-fix-api.component').then(
        (m) => m.KucoinIsNowAvailableThroughAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'kucoin-is-now-available-through-axon-trade-fix-api',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'kucoin-is-now-available-through-axon-trade-fix-api',
        fileName: '/kucoin-is-now-available-through-axon-trade-fix-api',
        title: 'KuCoin is now available through Axon Trade | Axon Trade',
        description:
          'Axon Trade OEMS for digital assets trading allows receiving normalized market data and actively trading on KuCoin\u0027s markets using unified FIX 4.4 end-point. ',
        keywords: 'kucoin fix api,kucoin trading api,kucoin hft',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-kucoin.png',
      },
    },
  },
  {
    path: 'bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors',
    loadComponent: () =>
      import('./pages/bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors/bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors.component').then(
        (m) => m.BitgetPartnersWithAxonTradeToOfferSeamlessTradingCorridorForInstitutionalInvestorsComponent,
      ),
    data: {
      sitemap: {
        loc: 'bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors',
        fileName: '/bitget-partners-with-axon-trade-to-offer-seamless-trading-corridor-for-institutional-investors',
        title: 'Bitget partners with Axon Trade | Axon Trade',
        description: 'Bitget partners with Axon Trade to offer seamless trading corridor for institutional investors',
        keywords: 'bitget fix api,bitget trading api,bitget algo trading',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-bitget.png',
      },
    },
  },
  {
    path: 'whitebit-is-now-available-through-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/whitebit-is-now-available-through-axon-trade-fix-api/whitebit-is-now-available-through-axon-trade-fix-api.component').then(
        (m) => m.WhitebitIsNowAvailableThroughAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'whitebit-is-now-available-through-axon-trade-fix-api',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'whitebit-is-now-available-through-axon-trade-fix-api',
        fileName: '/whitebit-is-now-available-through-axon-trade-fix-api',
        title: 'WhiteBIT is now available through Axon Trade FIX API | Axon Trade',
        description:
          'Axon Trade OEMS for digital assets trading allows receiving normalized market data and actively trading on WhiteBit\u0027s markets using unified FIX 4.4 end-point.',
        keywords: 'fix api,whitebit,crypto,cryptocurrency,crypto trading',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-whitebit.png',
      },
    },
  },
  {
    path: 'coinflex-is-now-available-through-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/coinflex-is-now-available-through-axon-trade-fix-api/coinflex-is-now-available-through-axon-trade-fix-api.component').then(
        (m) => m.CoinflexIsNowAvailableThroughAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'coinflex-is-now-available-through-axon-trade-fix-api',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'coinflex-is-now-available-through-axon-trade-fix-api',
        fileName: '/coinflex-is-now-available-through-axon-trade-fix-api',
        title: 'CoinFLEX is now available through Axon Trade FIX API | Axon Trade',
        description:
          'We are happy to announce that from now CoinFLEX is available through our OEMS. Both spot and derivatives markets can be accessed using the same FIX 4.4. API',
        keywords: 'fix api,crypto,cryptocurrency,derivatives,trading,crypto trading,coinflex',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-coinflex.png',
      },
    },
  },
  {
    path: 'axon-trade-opens-up-in-ty8-data-center',
    loadComponent: () =>
      import('./pages/axon-trade-opens-up-in-ty8-data-center/axon-trade-opens-up-in-ty8-data-center.component').then(
        (m) => m.AxonTradeOpensUpInTy8DataCenterComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-opens-up-in-ty8-data-center',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-opens-up-in-ty8-data-center',
        fileName: '/axon-trade-opens-up-in-ty8-data-center',
        title: 'Axon Trade Opens Up in TY8 Data Center | Axon Trade',
        description:
          'Axon Trade\u0027s OEMS for digital asset trading is now available in the Equinix TY8 location - Tokyo, Japan',
        keywords: 'oems for crypto,oems,crypto trading,ty8,ty8 data center',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-ty8-data-center.png',
      },
    },
  },
  {
    path: 'axon-trade-oems-for-crypto',
    loadComponent: () =>
      import('./pages/axon-trade-oems-for-crypto/axon-trade-oems-for-crypto.component').then(
        (m) => m.AxonTradeOemsForCryptoComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-oems-for-crypto',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-oems-for-crypto',
        fileName: '/axon-trade-oems-for-crypto',
        title: 'Axon Trade OEMS for Crypto | Axon Trade',
        description:
          'This article addresses the most common questions we received about Axon Trade OEMS for digital assets trading.',
        keywords: 'crypto,cryptocurrency,digital assets,crypto trading,oems,hft,high frequency trading,algotrading',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-oems-for-crypto.png',
      },
    },
  },
  {
    path: 'renko-based-scalper-for-binance-using-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/renko-based-scalper-for-binance-using-axon-trade-fix-api/renko-based-scalper-for-binance-using-axon-trade-fix-api.component').then(
        (m) => m.RenkoBasedScalperForBinanceUsingAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'renko-based-scalper-for-binance-using-axon-trade-fix-api',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'renko-based-scalper-for-binance-using-axon-trade-fix-api',
        fileName: '/renko-based-scalper-for-binance-using-axon-trade-fix-api',
        title: 'Scalper for Binance Using Axon Trade FIX API | Axon Trade',
        description: 'This article is an instruction on how to build a scalping application that trades on Binance.',
        keywords: 'binance,crypto trading,renko,fix api,crypto,cryptocurrency,algotrading,algo trading',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-binance-fix-api.png',
      },
    },
  },
  {
    path: 'how-to-use-axon-trade-fix-api-from-javascript-node-js',
    loadComponent: () =>
      import('./pages/how-to-use-axon-trade-fix-api-from-javascript-node-js/how-to-use-axon-trade-fix-api-from-javascript-node-js.component').then(
        (m) => m.HowToUseAxonTradeFixApiFromJavascriptNodeJsComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-to-use-axon-trade-fix-api-from-javascript-node-js',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-to-use-axon-trade-fix-api-from-javascript-node-js',
        fileName: '/how-to-use-axon-trade-fix-api-from-javascript-node-js',
        title: 'How to use Axon Trade FIX API from JavaScript/node.js | Axon Trade',
        description: 'A small demo on how to use Axon Trade FIX API from JavaScript/node.js.',
        keywords: 'fix api,javascript,crypto trading,algo trading,algotrading,crypto,cryptocurrency',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/how-to-use-xtrd-fix-api-from-javascript.png',
      },
    },
  },
  {
    path: 'bybit-is-now-available-through-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/bybit-is-now-available-through-axon-trade-fix-api/bybit-is-now-available-through-axon-trade-fix-api.component').then(
        (m) => m.BybitIsNowAvailableThroughAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'bybit-is-now-available-through-axon-trade-fix-api',
        lastmod: '2025-02-07',
      },
      meta: {
        loc: 'bybit-is-now-available-through-axon-trade-fix-api',
        fileName: '/bybit-is-now-available-through-axon-trade-fix-api',
        title: 'Bybit is now available through Axon Trade FIX API | Axon Trade',
        description: 'Bybit is available through Axon Trade FIX API.',
        keywords: 'crypto,crypto trading,bybit,fix api',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-bybit.png',
      },
    },
  },
  {
    path: 'changes-in-naming-convention-for-perpetual-swaps-futures-and-options',
    loadComponent: () =>
      import('./pages/changes-in-naming-convention-for-perpetual-swaps-futures-and-options/changes-in-naming-convention-for-perpetual-swaps-futures-and-options.component').then(
        (m) => m.ChangesInNamingConventionForPerpetualSwapsFuturesAndOptionsComponent,
      ),
    data: {
      sitemap: {
        loc: 'changes-in-naming-convention-for-perpetual-swaps-futures-and-options',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'changes-in-naming-convention-for-perpetual-swaps-futures-and-options',
        fileName: '/changes-in-naming-convention-for-perpetual-swaps-futures-and-options',
        title: 'Changes in Naming For Futures, and Options | Axon Trade',
        description: 'We changed the naming convention for perpetual swaps, futures, and options.',
        keywords: 'perpetual swaps,futures,options,crypto,crypto trading',
        og_type: 'article',
        imgUrl:
          '/assets/blog-card-images/axon-trade-changes-in-naming-convention-for-perpetual-swaps-futures-and-options.png',
      },
    },
  },
  {
    path: 'how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving',
    loadComponent: () =>
      import('./pages/how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving/how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving.component').then(
        (m) => m.HowToImproveExecutionInCryptoMarketsOrAxonTradeArtOfShavingComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving',
        fileName: '/how-to-improve-execution-in-crypto-markets-or-axon-trade-art-of-shaving',
        title: 'How to Improve Execution in Crypto Markets | Axon Trade',
        description:
          'An article by Axon Trade\u0027s Co-Founder and CTO Serg Gulko on improving execution in crypto markets.',
        keywords: 'high-frequency trading,crypto trading,crypto,cryptocurrency,low-latency',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-how-to-improve-execution-in-crypto-markets.png',
      },
    },
  },
  {
    path: 'axon-trade-opens-up-in-ld4-data-center',
    loadComponent: () =>
      import('./pages/axon-trade-opens-up-in-ld4-data-center/axon-trade-opens-up-in-ld4-data-center.component').then(
        (m) => m.AxonTradeOpensUpInLd4DataCenterComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-opens-up-in-ld4-data-center',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-opens-up-in-ld4-data-center',
        fileName: '/axon-trade-opens-up-in-ld4-data-center',
        title: 'Axon Trade Opens Up in LD4 Data Center | Axon Trade',
        description:
          'We are happy to announce that Axon Trade\u0027s OEMS for digital asset trading is now available in the Equinix LD4 location - London, UK!',
        keywords: 'oems,digital assets,crypto trading,data center,crypto',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-opens-up-in-ld-4-data-center.png',
      },
    },
  },
  {
    path: 'top-5-quant-trading-and-algotrading-blogs',
    loadComponent: () =>
      import('./pages/top-5-quant-trading-and-algotrading-blogs/top-5-quant-trading-and-algotrading-blogs.component').then(
        (m) => m.Top5QuantTradingAndAlgotradingBlogsComponent,
      ),
    data: {
      sitemap: {
        loc: 'top-5-quant-trading-and-algotrading-blogs',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'top-5-quant-trading-and-algotrading-blogs',
        fileName: '/top-5-quant-trading-and-algotrading-blogs',
        title: 'Top 5 Quant Trading and Algotrading Blogs | Axon Trade',
        description:
          'Top 5 Quant Trading and Algotrading Blogs\u2236 QuantInsti, Hackernoon, Quantpedia, Quantifiable Edges, Alvarez Quant Trading.',
        keywords: 'algo trading, algotrading, quant trading, crypto, crypto trading, industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/top-quant-trading-and-algotrading-blogs.png',
      },
    },
  },
  {
    path: 'coinbase-pro-api-review',
    loadComponent: () =>
      import('./pages/coinbase-pro-api-review/coinbase-pro-api-review.component').then(
        (m) => m.CoinbaseProApiReviewComponent,
      ),
    data: {
      sitemap: {
        loc: 'coinbase-pro-api-review',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'coinbase-pro-api-review',
        fileName: '/coinbase-pro-api-review',
        title: 'Coinbase Pro API Review | Axon Trade',
        description: 'Coinbase Pro API Review by Axon Trade\u0027s Co-Founder and CTO Serg Gulko',
        keywords: 'coinbase,api,crypto,crypto trading,crypto exchange',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/coinbase-pro-api-overview.png',
      },
    },
  },
  {
    path: 'different-deployment-options',
    loadComponent: () =>
      import('./pages/different-deployment-options/different-deployment-options.component').then(
        (m) => m.DifferentDeploymentOptionsComponent,
      ),
    data: {
      sitemap: {
        loc: 'different-deployment-options',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'different-deployment-options',
        fileName: '/different-deployment-options',
        title: 'Different deployment options | Axon Trade',
        description: '5 Reasons to Use Axon Trade\u2236 Different deployment options',
        keywords: 'crypto,crypto trading,digital assets,colocation,cross-connection,vpn,vps',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-to-use-axon-trade-different-deployment-options.png',
      },
    },
  },
  {
    path: 'engineering-axon-trade-fort-knox-of-authentication',
    loadComponent: () =>
      import('./pages/engineering-axon-trade-fort-knox-of-authentication/engineering-axon-trade-fort-knox-of-authentication.component').then(
        (m) => m.EngineeringAxonTradeFortKnoxOfAuthenticationComponent,
      ),
    data: {
      sitemap: {
        loc: 'engineering-axon-trade-fort-knox-of-authentication',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'engineering-axon-trade-fort-knox-of-authentication',
        fileName: '/engineering-axon-trade-fort-knox-of-authentication',
        title: 'Axon Trade\u0027s Fort Knox of Authentication | Axon Trade',
        description: '5 Reasons to Use Axon Trade\u2236 Engineering Axon Trade\u0027s Fort Knox of Authentication',
        keywords: 'security,crypto,crypto trading,digital assets',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-to-use-axon-trade-fort-knox-of-authentification.png',
      },
    },
  },
  {
    path: 'normalized-api-for-trading',
    loadComponent: () =>
      import('./pages/normalized-api-for-trading/normalized-api-for-trading.component').then(
        (m) => m.NormalizedApiForTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'normalized-api-for-trading',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'normalized-api-for-trading',
        fileName: '/normalized-api-for-trading',
        title: 'Normalized API for trading | Axon Trade',
        description: '5 Reasons to Use Axon Trade\u2236 Normalized API for trading',
        keywords: 'api,crypto,crypto trading,crypto exchanges,digital assets',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-to-use-axon-trade-normalized-api-for-trading.png',
      },
    },
  },
  {
    path: 'normalized-symbology',
    loadComponent: () =>
      import('./pages/normalized-symbology/normalized-symbology.component').then((m) => m.NormalizedSymbologyComponent),
    data: {
      sitemap: {
        loc: 'normalized-symbology',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'normalized-symbology',
        fileName: '/normalized-symbology',
        title: 'Normalized symbology | Axon Trade',
        description: '5 Reasons to Use Axon Trade\u2236  Normalized symbology',
        keywords: 'market data,crypto,cryptocurrency,crypto trading,digital assets',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-to-use-axon-trade-normalized-symbology.png',
      },
    },
  },
  {
    path: 'reliable-market-data',
    loadComponent: () =>
      import('./pages/reliable-market-data/reliable-market-data.component').then((m) => m.ReliableMarketDataComponent),
    data: {
      sitemap: {
        loc: 'reliable-market-data',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'reliable-market-data',
        fileName: '/reliable-market-data',
        title: 'Reliable Market Data | Axon Trade',
        description: '5 Reasons to Use Axon Trade\u2236  Reliable Market Data',
        keywords: 'market data,crypto,crypto trading,digital assets,cryptocurrency',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-to-use-axon-trade-reliable-market-data.png',
      },
    },
  },
  {
    path: 'how-to-trade-on-huobi-using-axon-trade-fix-api',
    loadComponent: () =>
      import('./pages/how-to-trade-on-huobi-using-axon-trade-fix-api/how-to-trade-on-huobi-using-axon-trade-fix-api.component').then(
        (m) => m.HowToTradeOnHuobiUsingAxonTradeFixApiComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-to-trade-on-huobi-using-axon-trade-fix-api',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-to-trade-on-huobi-using-axon-trade-fix-api',
        fileName: '/how-to-trade-on-huobi-using-axon-trade-fix-api',
        title: 'How to trade on Huobi using Axon Trade FIX API | Axon Trade',
        description: 'How to trade on Huobi using Axon Trade FIX API',
        keywords: 'huobi,crypto,cryptocurrency,crypto trading,digital assets',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-huobi-fix-api.png',
      },
    },
  },
  {
    path: 'recap-after-europe-trading-show-2019',
    loadComponent: () =>
      import('./pages/recap-after-europe-trading-show-2019/recap-after-europe-trading-show-2019.component').then(
        (m) => m.RecapAfterEuropeTradingShow2019Component,
      ),
    data: {
      sitemap: {
        loc: 'recap-after-europe-trading-show-2019',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'recap-after-europe-trading-show-2019',
        fileName: '/recap-after-europe-trading-show-2019',
        title: 'Recap after Europe Trading Show 2019 | Axon Trade',
        description: 'For Axon Trade, the Trading Show has become a must-attend event. This year we visited London!',
        keywords: 'trading show,crypto trading,crypto exchanges',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-recap-after-europe-trading-show-2019.png',
      },
    },
  },
  {
    path: 'the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019',
    loadComponent: () =>
      import('./pages/the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019/the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019.component').then(
        (m) => m.TheAxonTradeTeamWillTakePartInTheTradingShowEurope2019Component,
      ),
    data: {
      sitemap: {
        loc: 'the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019',
        fileName: '/the-axon-trade-team-will-take-part-in-the-trading-show-europe-2019',
        title: 'The Axon Trade team will take part in Trading Show Europe | Axon Trade',
        description:
          'The Axon Trade team will take part in The Trading Show in London on October 17, 2019 as an exhibitor and sponsor.',
        keywords: 'trading show,crypto trading,exchanges,crypto exchanges',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-trading-show-2020.png',
      },
    },
  },
  {
    path: '5-reasons-why-we-need-more-interconnected-crypto-exchanges',
    loadComponent: () =>
      import('./pages/five-reasons-why-we-need-more-interconnected-crypto-exchanges/five-reasons-why-we-need-more-interconnected-crypto-exchanges.component').then(
        (m) => m.FiveReasonsWhyWeNeedMoreInterconnectedCryptoExchangesComponent,
      ),
    data: {
      sitemap: {
        loc: '5-reasons-why-we-need-more-interconnected-crypto-exchanges',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: '5-reasons-why-we-need-more-interconnected-crypto-exchanges',
        fileName: '/5-reasons-why-we-need-more-interconnected-crypto-exchanges',
        title: 'Why We Need More Interconnected Crypto Exchanges | Axon Trade',
        description: '5 Reasons Why We Need More Interconnected Crypto Exchanges',
        keywords: 'crypto exchanges,cryptocurrency,institutional traders,liquidity,industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/5-reasons-why-we-need-more-interconnected-crypto-exchanges.png',
      },
    },
  },
  {
    path: '6-factors-feared-by-institutional-investors-in-cryptocurrency',
    loadComponent: () =>
      import('./pages/six-factors-feared-by-institutional-investors-in-cryptocurrency/six-factors-feared-by-institutional-investors-in-cryptocurrency.component').then(
        (m) => m.SixFactorsFearedByInstitutionalInvestorsInCryptocurrencyComponent,
      ),
    data: {
      sitemap: {
        loc: '6-factors-feared-by-institutional-investors-in-cryptocurrency',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: '6-factors-feared-by-institutional-investors-in-cryptocurrency',
        fileName: '/6-factors-feared-by-institutional-investors-in-cryptocurrency',
        title: '6 Factors Feared by Institutional Investors in Crypto | Axon Trade',
        description: 'Six factors affecting the appetite of institutional investors in cryptocurrency.',
        keywords: 'institutional crypto traders,institutional investors,crypto traders,banks,industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/6-factors-feared-by-institutional-investors-in-cryptocurrency.png',
      },
    },
  },
  {
    path: 'axon-trade-team-will-take-part-in-the-trading-show-new-york-2019',
    loadComponent: () =>
      import('./pages/axon-trade-team-will-take-part-in-the-trading-show-new-york-2019/axon-trade-team-will-take-part-in-the-trading-show-new-york-2019.component').then(
        (m) => m.AxonTradeTeamWillTakePartInTheTradingShowNewYork2019Component,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-team-will-take-part-in-the-trading-show-new-york-2019',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-team-will-take-part-in-the-trading-show-new-york-2019',
        fileName: '/axon-trade-team-will-take-part-in-the-trading-show-new-york-2019',
        title: 'The Axon Trade team will take part in Trading Show New York | Axon Trade',
        description:
          'The Axon Trade team will take part in The Trading Show in New York on September 25, 2019 as an exhibitor and sponsor.',
        keywords: 'trading show,crypto trading,crypto exchanges,exchange technology',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-trading-show-2019.png',
      },
    },
  },
  {
    path: 'how-institutional-traders-would-benefit-from-global-crypto-exchanges',
    loadComponent: () =>
      import('./pages/how-institutional-traders-would-benefit-from-global-crypto-exchanges/how-institutional-traders-would-benefit-from-global-crypto-exchanges.component').then(
        (m) => m.HowInstitutionalTradersWouldBenefitFromGlobalCryptoExchangesComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-institutional-traders-would-benefit-from-global-crypto-exchanges',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-institutional-traders-would-benefit-from-global-crypto-exchanges',
        fileName: '/how-institutional-traders-would-benefit-from-global-crypto-exchanges',
        title: 'Institutional Traders and Global Crypto Exchanges | Axon Trade',
        description:
          'Axon Trade is building new financial products to improve access to global crypto exchanges for institutional investors',
        keywords: 'exchange,cryptocurrency,crypto,traders,institutional traders,industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/how-institutional-traders-would-benefit-from-global-crypto-exchanges.png',
      },
    },
  },
  {
    path: 'axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services',
    loadComponent: () =>
      import('./pages/axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services/axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services.component').then(
        (m) => m.AxonTradeAndCexIoAnnounceIntegrationPartnershipOfferingInstitutionalGradeTradingServicesComponent,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services',
        fileName:
          '/axon-trade-and-cex-io-announce-integration-partnership-offering-institutional-grade-trading-services',
        title: 'XTRD and CEX.IO Institutional-Grade Trading Services | Axon Trade',
        description:
          'The partnership will integrate existing and future XTRD trading products with CEX.IO and create a CEX.IO institutional services division',
        keywords: 'institutional crypto traders,institutional investors,cryptocurrency,bitcoin',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-cex.io-partnership.png',
      },
    },
  },
  {
    path: 'how-institutional-investment-will-change-the-crypto-asset-market',
    loadComponent: () =>
      import('./pages/how-institutional-investment-will-change-the-crypto-asset-market/how-institutional-investment-will-change-the-crypto-asset-market.component').then(
        (m) => m.HowInstitutionalInvestmentWillChangeTheCryptoAssetMarketComponent,
      ),
    data: {
      sitemap: {
        loc: 'how-institutional-investment-will-change-the-crypto-asset-market',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'how-institutional-investment-will-change-the-crypto-asset-market',
        fileName: '/how-institutional-investment-will-change-the-crypto-asset-market',
        title: 'How Institutional Investment Will Change the Crypto Market | Axon Trade',
        description: 'The good and bad of institutional investment in the crypto market.',
        keywords: 'institutional investment,crypto,blockchain,institutional traders,cryptocurrency',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-how-to-improve-execution-in-crypto-asset-markets.png',
      },
    },
  },
  {
    path: 'rest-ws-vs-fix-for-crypto-trading',
    loadComponent: () =>
      import('./pages/rest-ws-vs-fix-for-crypto-trading/rest-ws-vs-fix-for-crypto-trading.component').then(
        (m) => m.RestWsVsFixForCryptoTradingComponent,
      ),
    data: {
      sitemap: {
        loc: 'rest-ws-vs-fix-for-crypto-trading',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'rest-ws-vs-fix-for-crypto-trading',
        fileName: '/rest-ws-vs-fix-for-crypto-trading',
        title: 'REST/Ws vs FIX for crypto trading | Axon Trade',
        description: 'Axon Trade CTO Serg Gulko on differences between REST/Ws and FIX for crypto trading',
        keywords: 'rest api,ws,fix api,crypto trading,industry',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/REST-WS-vs-FIX-for-Crypto-Trading.png',
      },
    },
  },
  {
    path: 'axon-trade-update-october-31-2018',
    loadComponent: () =>
      import('./pages/axon-trade-update-october-31-2018/axon-trade-update-october-31-2018.component').then(
        (m) => m.AxonTradeUpdateOctober312018Component,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-update-october-31-2018',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-update-october-31-2018',
        fileName: '/axon-trade-update-october-31-2018',
        title: 'XTRD Update\u2236 October 31, 2018 | Axon Trade',
        description:
          'Summary\u2236 Product update, New York Trading Show recap, VOA\u0027s Interview with XTRD CTO Serg Gulko, Miami Blockchain Shift Update, Weekly CTO Q&A sessions',
        keywords: 'trading show,product update,crypto trading',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-product-update-october-2018.png',
      },
    },
  },
  {
    path: 'blockchain-shift-conference-update',
    loadComponent: () =>
      import('./pages/blockchain-shift-conference-update/blockchain-shift-conference-update.component').then(
        (m) => m.BlockchainShiftConferenceUpdateComponent,
      ),
    data: {
      sitemap: {
        loc: 'blockchain-shift-conference-update',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'blockchain-shift-conference-update',
        fileName: '/blockchain-shift-conference-update',
        title: 'Blockchain Shift Conference Update | Axon Trade',
        description: 'Axon Trade recently attended the Miami Blockchain Shift conference on October 11–12',
        keywords: 'institutional traders,distributed ledger technology,fintech,events,videos',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-blockchain-shift-conference-update.png',
      },
    },
  },
  {
    path: 'new-york-trading-show-recap',
    loadComponent: () =>
      import('./pages/new-york-trading-show-recap/new-york-trading-show-recap.component').then(
        (m) => m.NewYorkTradingShowRecapComponent,
      ),
    data: {
      sitemap: {
        loc: 'new-york-trading-show-recap',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'new-york-trading-show-recap',
        fileName: '/new-york-trading-show-recap',
        title: 'New York Trading Show recap | Axon Trade',
        description: 'Axon Trade Attended The New York Trading Show 2018',
        keywords: 'trading show,crypto,crypto trading,cryptocurrency,events',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-new-york-trading-show-recap.png',
      },
    },
  },
  {
    path: 'axon-trade-product-update-october-11-2018',
    loadComponent: () =>
      import('./pages/axon-trade-product-update-october-11-2018/axon-trade-product-update-october-11-2018.component').then(
        (m) => m.AxonTradeProductUpdateOctober112018Component,
      ),
    data: {
      sitemap: {
        loc: 'axon-trade-product-update-october-11-2018',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'axon-trade-product-update-october-11-2018',
        fileName: '/axon-trade-product-update-october-11-2018',
        title: 'Axon Trade product update— October 11, 2018 | Axon Trade',
        description: 'Axon Trade product update — October 11, 2018',
        keywords: 'equity,fx,product update,product updates',
        og_type: 'article',
        imgUrl: '/assets/blog-card-images/axon-trade-product-update-october-2018-1.png',
      },
    },
  },
  {
    path: 'top-100-blockchain-and-crypto-influencers-on-twitter-to-follow',
    loadComponent: () =>
      import('./pages/top-100-blockchain-and-crypto-influencers-on-twitter-to-follow/top-100-blockchain-and-crypto-influencers-on-twitter-to-follow.component').then(
        (m) => m.Top100BlockchainAndCryptoInfluencersOnTwitterToFollowComponent,
      ),
    data: {
      sitemap: {
        loc: 'top-100-blockchain-and-crypto-influencers-on-twitter-to-follow',
        lastmod: '2024-11-06',
      },
      meta: {
        loc: 'top-100-blockchain-and-crypto-influencers-on-twitter-to-follow',
        fileName: '/top-100-blockchain-and-crypto-influencers-on-twitter-to-follow',
        title: 'Top 100 Blockchain and Crypto Influencers on Twitter | Axon Trade',
        description:
          'The Axon Trade team has compiled a list of Twitter accounts you probably would be interested in following if you work in blockchain & crypto related fields',
        keywords: 'twitter,influencers,crypto,industry',
        og_type: 'article',
        imgUrl:
          '/assets/blog-card-images/axon-trade-top-100-blockchain-and-crypto-influencers-on-twitter-to-follow.png',
      },
    },
  },
];
